/**
 * ISO clause reference data — the single source for the whole repo.
 *
 * MOVED FROM THE BROWSER 2026-08-16. Both structures below lived only in
 * apps/web/src/pages/accreditations/AccreditationsPage.tsx, which meant the
 * Accreditations audit-review CSV had to be assembled in the page — so nothing
 * recorded that an ISO evidence pack had been exported, and the export could
 * not follow the platform standard that files come from the server.
 *
 * Shane, 2026-08-16: "we need to move the accreditation to the server as well
 * so all is correct... it will all be there as we said STANDARD", and the
 * reason it was worth doing now rather than later: "we have not really started
 * the ISO yet". Reference data is cheapest to move before anything depends on
 * it.
 *
 * Copied VERBATIM, not retyped — 65 clauses across four standards plus the
 * guidance pack. The web now reads these through the API rather than holding
 * its own copy, because two copies of an ISO clause map is a drift waiting to
 * happen: the file and the screen would quietly stop agreeing and nothing
 * would say which was right.
 *
 * It lives in @jeanieiq/types rather than in the API because BOTH sides need
 * it: the API to build the audit-review CSV, the web to render the clause map
 * and guidance tabs. A shared module is one copy imported twice; an API-only
 * module would have forced the web to keep its own, which is the drift this
 * move exists to prevent.
 *
 * Reference data, not tenant data — the same for every tenant, so it carries
 * no tenantId and needs no RLS.
 */

/** The four standards the platform carries. Mirrors the Prisma enum. */
export type IsoStandard = 'iso_9001' | 'iso_14001' | 'iso_27001' | 'iso_45001';

/**
 * Runtime list of the standards, for validation and pickers — one source so a
 * new standard is added in a single place. Order is the display order.
 */
export const ISO_STANDARDS: readonly IsoStandard[] = [
  'iso_9001',
  'iso_14001',
  'iso_27001',
  'iso_45001',
];

/**
 * The clauses each standard shares with the others: the same number AND the
 * same requirement, from the harmonised structure every management-system
 * standard is written to. A clause outside a standard's list either means
 * something else there (5.2, 6.2, 8.2, 9.1.x) or does not exist there (8.4),
 * so a record never crosses to it. 27001:2022 numbers improvement differently
 * (10.1 continual improvement, no 10.3), so those stay out of its list.
 */
const HARMONISED_CORE = [
  '4', '4.1', '4.2', '4.3', '4.4',
  '5.1', '5.3',
  '6.1',
  '7.1', '7.2', '7.3', '7.4', '7.5', '7.5.1', '7.5.2', '7.5.3',
  '8.1',
  '9.2', '9.3',
] as const;
export const CLAUSES_SHARED_BY_STANDARD: Record<IsoStandard, readonly string[]> = {
  iso_9001: [...HARMONISED_CORE, '10.1', '10.2', '10.3'],
  iso_14001: [...HARMONISED_CORE, '10.1', '10.2', '10.3'],
  iso_45001: [...HARMONISED_CORE, '10.1', '10.2', '10.3'],
  iso_27001: [...HARMONISED_CORE, '10.2'],
};

/**
 * The standards one record files its clause under: the standard it is for,
 * while the company holds it, and each other held standard that shares the
 * clause. With no standard held, ISO 9001.
 */
export function standardsForClause(
  clause: string,
  held: readonly string[],
  forStandard: IsoStandard = 'iso_9001',
): IsoStandard[] {
  const holding = ISO_STANDARDS.filter((s) => held.includes(s));
  const pool: readonly IsoStandard[] = holding.length > 0 ? holding : ['iso_9001'];
  const shared = (s: IsoStandard): boolean => CLAUSES_SHARED_BY_STANDARD[s].includes(clause);
  return ISO_STANDARDS.filter(
    (s) => pool.includes(s) && (s === forStandard || (shared(s) && shared(forStandard))),
  );
}

/** Short human labels for the standards, for admin pickers and chips. */
export const ISO_STANDARD_LABELS: Record<IsoStandard, string> = {
  iso_9001: 'ISO 9001',
  iso_14001: 'ISO 14001',
  iso_27001: 'ISO 27001',
  iso_45001: 'ISO 45001',
};

export interface ClauseMapRow {
  clause: string;
  title: string;
  description: string;
  /** Owning JeanieIQ section/tab (human label). */
  area: string;
  /** Confirmed SPA route for the deep link, or null = document-based. */
  to: string | null;
}

/**
 * Sort clause rows the way a person reads a standard: 7.1.3 before 7.2, and 9.3
 * before 10.1.
 *
 * Numeric collation, not string order — plain sorting puts 10.1 before 2 and
 * 7.2 before 7.1.3, which is how a clause list stops looking like the standard
 * it came from. The same comparator `AccreditationsPage` already uses for its
 * clause picker, so the list and the picker agree.
 *
 * Applied at definition so entries can be appended to the end of an array
 * without hunting for their numeric slot — which is where a new clause quietly
 * ends up in the wrong place.
 */
const byClauseNumber = (a: ClauseMapRow, b: ClauseMapRow): number =>
  a.clause.localeCompare(b.clause, undefined, { numeric: true });

const ISO_9001_CLAUSE_MAP: ClauseMapRow[] = [
  { clause: '4', title: 'Context of the organisation', description: 'Set out in clause 4 of the Business Management System: approving an issue that answers any part of it files this clause. Finalising a Context review files it too.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '5.1', title: 'Leadership & commitment', description: 'A management review chaired by top management, with the decisions and resource commitments it produced. Finalising one files this clause.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '5.2', title: 'Quality policy', description: 'Publishing the quality policy in the Company Policy Library files this clause: published is in use. Approving its issue on the Document Register files nothing, and archiving the policy withdraws it. The policy carries its clause on the Document Register.', area: 'HR & People · Policies', to: '/hr?tab=policies' },
  { clause: '6.1', title: 'Actions to address risks & opportunities', description: 'The Risks & Opportunities register: each risk or opportunity with its action and owner. Recording an effectiveness review files this clause.', area: 'Accreditations · Risks & Opportunities', to: '/accreditations?tab=iso&isoSub=risks' },
  { clause: '6.2', title: 'Quality objectives & planning', description: 'Measurable quality objectives with their plan, in the Objectives register. Recording how one was communicated (a finalised meeting, an approved document, or a published memo or policy for staff to sign) files the evidence.', area: 'Accreditations · Objectives', to: '/accreditations' },
  { clause: '7.1.5', title: 'Monitoring & measuring resources', description: 'Logging the certificate the calibration company issued, with a calibration record, files this clause. A calibration saved without its certificate files nothing.', area: 'Plant & Tools · Calibration Records', to: '/plant?tab=calibration' },
  { clause: '7.2', title: 'Competence', description: 'An employee signing a toolbox talk files this clause: on the Driver Tablet, or by name at a shared screen. So does an appraisal once it is signed and complete. The first signature files; a repeat, a failed question-test, and a talk assigned or delivered but not signed file nothing.', area: 'Training Records · Toolbox Talks · Driver Tablet', to: '/training' },
  { clause: '7.3', title: 'Awareness', description: 'Signing a policy, or completing an awareness course with its certificate, files this clause against the employee. A completion without its certificate files nothing.', area: 'HR & People · Policies · Training & Certificates', to: '/hr?tab=policies' },
  { clause: '7.5', title: 'Documented information', description: 'Controlled documents, versions and distribution — the ISO Documents library.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '8.1', title: 'Operational planning & control', description: 'Moving a job from its first stage into a working stage files this clause: the work planned and released under control.', area: 'Your operational vertical · Jobs', to: null },
  /*
   * "YOUR OPERATIONAL VERTICAL" IS DELIBERATE, AND SO IS `to: null` — 2026-08-30.
   *
   * These clauses used to name Enquiries & Quotations and Jobs. SPEC-51 Stage 10
   * retired both section keys, and a static label cannot replace them with one
   * name, because a tenant CHOOSES its operational area — Standard Sales & Jobs,
   * Motor Trade, Switchboard Builder. Shane: *"in the vertical there is a number
   * of operations that can be selected to suit tennents"*, and the platform's own
   * note is that the verticals are alternatives rather than layers.
   *
   * So naming Standard Sales & Jobs here would have been accurate only for the
   * tenants that happen to run it, and quietly wrong for everyone else — a clause
   * map that sends a Switchboard tenant to a section they do not have is worse
   * than one that describes the place in general terms.
   *
   * `to: null` follows for the same reason: the deep link had to point at ONE
   * route, and the right route differs per tenant. Null already means
   * "document-based" here, so the row still reads correctly. A tenant-aware link
   * is real work rather than a label change — `ISO-CLAUSE-MAP-VERTICAL-LINK-001`.
   *
   * 8.7 keeps `/fleet`: Fleet is its own section, held by any tenant regardless
   * of vertical, so that link was never the stale part — only the "Jobs ·" prefix.
   *
   * Pharmacy is out of scope on purpose. Shane: *"pharmacy has its own standard
   * to adhere to not iso"* — those tenants are not evidencing ISO 9001 at all, so
   * no label here is the answer for them.
   */
  { clause: '8.2.3', title: 'Review of the requirements for products & services', description: 'Winning a quotation files the contract review automatically — the customer must be approved and on agreed terms before it can be won, so the record is of a review that genuinely happened.', area: 'Your operational vertical · Quotations', to: null },
  { clause: '8.4', title: 'Control of externally provided processes', description: 'The approved-supplier register and its vetting gate evidence external-provider control.', area: 'Approved Suppliers', to: '/approved-suppliers' },
  { clause: '8.4.1', title: 'External providers — evaluation & selection', description: 'Approving a supplier, and reviewing its performance, files this clause with the approver and date. Approving a customer is 8.2.3, not this.', area: 'Approved Suppliers · Customer CRM', to: '/approved-suppliers' },
  { clause: '8.5.1', title: 'Control of production & service provision', description: 'Moving a job from its first stage into work files this clause, and so does reviewing a production order, once: an approval evidences the control, a query that the control refuses things.', area: 'Your operational vertical · Jobs and Production', to: null },
  { clause: '8.6', title: 'Release of products & services', description: 'Moving a job to its completing stage, or confirming works complete on its close-out, files the release record once, naming who authorised it. Moving it back, or unticking, withdraws the evidence rather than deleting it.', area: 'Your operational vertical · Close-out', to: null },
  { clause: '8.7', title: 'Control of nonconforming outputs', description: 'A queried production order, a defect found after handover once it is closed, and a corrective action about a product once it is closed, all file this clause — the fault and what was done about it.', area: 'Your operational vertical · Production · Jobs', to: null },
  { clause: '9.1', title: 'Monitoring, measurement, analysis & evaluation', description: 'Evidenced by its parts: measurements against quality objectives (9.1.1), customer satisfaction (9.1.2) and the analysis at management review (9.1.3).', area: 'Accreditations · Objectives, Customer Feedback, Management Review', to: '/accreditations' },
  { clause: '9.1.1', title: 'Monitoring & measurement', description: 'Every measurement recorded against an active quality objective files the value, the target and the method, so the trend itself is the evidence. Each objective says how often it is measured; the register shows when the next is due and an overdue one raises a notice.', area: 'Accreditations · Objectives', to: '/accreditations' },
  { clause: '9.1.2', title: 'Customer satisfaction', description: 'Compliments and complaints file this clause as they are logged in the complaints log; a quarterly satisfaction survey response files it when a director or manager marks it reviewed.', area: 'Accreditations · Customer Feedback', to: '/accreditations' },
  { clause: '9.1.3', title: 'Analysis & evaluation', description: 'Filed when a management review is finalised WITH its inputs recorded. This is the only source of 9.1.3 evidence on the platform, so an empty inputs box leaves the clause with nothing.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '9.2', title: 'Internal audit', description: 'The annual internal-audit programme, audits and findings in the Internal Audit module. Completing an audit, with its scope, criteria, impartial auditor and results, files this clause; a nonconformity it finds is raised as a corrective action.', area: 'Accreditations · Internal Audit', to: '/accreditations' },
  { clause: '9.3', title: 'Management review', description: 'The annual management review and its tracked actions in the Management Review module.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '10.1', title: 'Improvement — general', description: 'Opportunities for improvement determined and selected at the management review, filed when the review records outputs or raises actions.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '10.2', title: 'Nonconformity & corrective action', description: 'Nonconformities in the Non-Conformance & Corrective Action register: the correction, the root cause, whether similar ones exist or could occur, what was updated in risks or the system, the actions and the effectiveness review. Closing one, with all of those recorded, files this clause.', area: 'Accreditations · Corrective Actions', to: '/accreditations' },
  { clause: '10.3', title: 'Continual improvement', description: 'Improvement actions tracked through the corrective-action and management-review records.', area: 'Accreditations · ISO Documents', to: '/accreditations' },

  /*
   * ═══════════════════════════════════════════════════════════════════════
   * THE REST OF ISO 9001:2015 — added 2026-08-29.
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Shane, tagging his register: *"all i am asking is you to add the extra iso
   * clauses into the code and it fileters onto the relevant documents... you
   * highlighted areas were missing in the code so they need adding in"*.
   *
   * The map held 25 clauses — the ones a JeanieIQ section evidences
   * automatically. Everything else in the standard was simply absent, so a
   * document answering it had nowhere to point. Mapping a real register showed
   * how much that costs: the clause a driving policy answers is 7.1.3
   * Infrastructure, whose note names transportation resources; a COSHH memo
   * answers 7.1.4 Environment for the operation of processes; a data-processing
   * agreement answers 7.5.3, protection from loss of confidentiality. None of
   * those three existed here.
   *
   * WHY THE ABSENCE WAS EASY TO MISS. Evidence filed against an unmapped clause
   * is NOT lost — `AccreditationsPage` builds its rollup from the evidence and
   * lists anything outside this map under `extraClauses`. So it appears, without
   * a title, without guidance, and outside the clause picker. It works and it
   * looks like an oddity rather than a gap.
   *
   * `to: null` on most of these is deliberate and is what the field means:
   * "document-based". These are clauses a tenant answers by holding a controlled
   * document, not by a screen filling itself in. Claiming a route would send an
   * auditor to a page that shows them nothing.
   *
   * ORDER DOES NOT MATTER HERE — the array is sorted by clause below, so new
   * entries can be appended without hunting for their numeric slot.
   */

  // ── 4 Context of the organisation ──────────────────────────────────────
  { clause: '4.1', title: 'Understanding the organisation and its context', description: 'The legislation the business has identified and works to, listed in 4.1 of the Business Management System from the Legal Register: approving an issue that lists it files this clause. Finalising a Context review with its internal and external issues recorded files it too.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.2', title: 'Needs and expectations of interested parties', description: 'Who has a stake in the organisation and what they require — customers, employees, shareholders, regulators. 4.2 of the Business Management System lists them from the latest finalised Context review: approving an issue that lists them files this clause, and so does finalising a Context review with them recorded.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.3', title: 'Determining the scope of the management system', description: 'What the management system covers, and any requirement judged not applicable with the reason. Approving the Business Management System files this clause when the scope of registration is filled in on BMS Manual; with that box empty it files nothing.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.4', title: 'The management system and its processes', description: 'The processes the system needs, their sequence, and how they are measured and improved. Approving the Business Management System files this clause when the quality processes (4.4, Part 1 where the company also holds ISO 14001) are filled in on BMS Manual; with that box empty it files nothing.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },

  // ── 5 Leadership ───────────────────────────────────────────────────────
  { clause: '5.1.2', title: 'Customer focus', description: 'Top management reviewing customer satisfaction and feedback at the management review, and deciding what to do about it. Answering that review input files this clause.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '5.3', title: 'Roles, responsibilities and authorities', description: 'Who is responsible for what, assigned and communicated. Publishing the organisation chart files this clause: published is in use. A director or an admin publishes it; an empty chart, or one with nobody at the top, cannot be published.', area: 'Administration · Organisation Chart', to: '/admin/org-chart' },

  // ── 6 Planning ─────────────────────────────────────────────────────────
  { clause: '6.3', title: 'Planning of changes', description: 'Planned changes to the quality system, in the Changes register: why, what it could affect, keeping the system whole, the resources, who is responsible and by when. Marking a change done files this clause; cancelling withdraws it.', area: 'Accreditations · Changes', to: '/accreditations?tab=iso&isoSub=changes' },

  // ── 7 Support ──────────────────────────────────────────────────────────
  { clause: '7.1.1', title: 'Resources — general', description: 'The resources the management system needs, and where they come from. Finalising a chaired management review that answered "Adequacy of resources" files this clause.', area: 'Accreditations · Management Review', to: '/accreditations?tab=iso&isoSub=mgmtreview' },
  { clause: '7.1.2', title: 'People', description: 'Determining and providing the people necessary to run the system and its processes. A vacancy raised in Recruitment is a person the business decided it needs; marking it Filled files this clause, and reopening or deleting it withdraws that.', area: 'HR & People · Recruitment', to: '/recruitment' },
  { clause: '7.1.3', title: 'Infrastructure', description: 'Buildings and utilities, equipment including hardware and software, and transportation resources. Covers premises, machinery and vehicles. A passed plant inspection, and a vehicle defect with the repair logged against it, file this clause.', area: 'Building Maintenance · Plant & Equipment · Fleet', to: null },
  { clause: '7.1.4', title: 'Environment for the operation of processes', description: 'The conditions people work in — physical factors such as heat, noise and hygiene, and the social and psychological ones the clause note names, including a non-discriminatory and non-confrontational environment. Publishing a policy, or sending out a memo, whose register entry carries 7.1.4 files this clause; library policies and memos name it in their Legal basis. Approving a risk assessment on the Document Register files it too.', area: 'Document Register · Policies · Memos', to: '/admin/documents' },
  { clause: '7.1.6', title: 'Organisational knowledge', description: 'The knowledge the organisation needs to run its processes, how it is kept, and how it is passed on. A work instruction, SOP, method statement or RAMS on the Document Register answers this clause; approving it files it.', area: 'Document Register', to: '/admin/documents' },
  { clause: '7.4', title: 'Communication', description: 'What is communicated about the management system, when, with whom, how and by whom, inside and outside the company: the Business Management System\'s Communication section. Finalising a meeting files this clause, and so does sending out a memo. Communication with customers and suppliers is evidenced where it happens: complaints and feedback (8.2.1), quotations issued (8.2.2, 8.2.4) and purchase orders sent (8.4.3).', area: 'Accreditations · Communication · Memos', to: '/accreditations?tab=iso&isoSub=communication' },
  { clause: '7.5.1', title: 'Documented information — general', description: 'The documented information the standard requires, plus whatever else the organisation decides it needs. Approving the Business Management System files this clause.', area: 'Accreditations · ISO Documents', to: '/admin/documents' },
  { clause: '7.5.2', title: 'Creating and updating', description: 'Identification, format and approval of documented information — the number, title, issue and approver a controlled document carries. Approving a version on the Document Register files this clause.', area: 'Accreditations · ISO Documents', to: '/admin/documents' },
  { clause: '7.5.3', title: 'Control of documented information', description: 'Availability where needed and protection — from loss of confidentiality, improper use or loss of integrity — plus distribution, storage, version control and retention. Withdrawing a document from use files this clause.', area: 'Accreditations · ISO Documents', to: '/admin/documents' },

  // ── 8 Operation ────────────────────────────────────────────────────────
  { clause: '8.2', title: 'Requirements for products and services', description: 'What the customer asks for, what the law requires, and what the organisation commits to.', area: 'Your operational vertical · Requirements', to: null },
  { clause: '8.2.1', title: 'Customer communication', description: 'Enquiries, contracts, orders, changes, feedback and complaints, and how customer property is handled.', area: 'Customers', to: null },
  { clause: '8.2.2', title: 'Determining the requirements', description: 'Establishing what is being offered, including anything the law requires, before committing to supply it. Issuing the first revision of a quotation, or sending its first version, files this clause.', area: 'Your operational vertical · Requirements', to: null },
  { clause: '8.2.4', title: 'Changes to requirements', description: 'Amended requirements captured, and the people who need to know told. Issuing a later revision of a quotation, or sending a revised version, files this clause.', area: 'Your operational vertical · Requirements', to: null },
  { clause: '8.3', title: 'Design and development', description: 'Where the organisation designs what it supplies: planning, inputs, controls, outputs and changes. Frequently excluded under 4.3 where design is not carried out; excluding it excludes its sub-clauses with it.', area: 'Accreditations · ISO Documents', to: null },
  { clause: '8.3.3', title: 'Design and development inputs', description: 'Saving an extracted spec brief in the switchboard builder files this clause: the customer\'s specification read, and the key facts a person confirmed from it. A document merely arriving in the folder files nothing.', area: 'Switchboard Builder · Spec review', to: '/enquiries-quotations' },
  { clause: '8.3.4', title: 'Design and development controls', description: 'The customer approving a design files this clause: a quotation won on a revision, or a variation order accepted against one, in the switchboard builder. The customer inspecting and approving the layout is the design check; there is no separate internal sign-off. A win or order that names no revision files nothing.', area: 'Switchboard Builder · Revisions', to: '/enquiries-quotations' },
  { clause: '8.3.6', title: 'Design and development changes', description: 'Opening a revision after the first, in the switchboard builder, files this clause: the revision it changes, why it was opened, who asked and the contact. The first revision is the design starting and files nothing.', area: 'Switchboard Builder · Revisions', to: '/enquiries-quotations' },
  { clause: '8.3.5', title: 'Design and development outputs', description: 'Saving a revision in the switchboard builder files this clause once the quotation document, technical submittal, drawing packs and parts kits carry its number. A revision closed with none of them files nothing.', area: 'Switchboard Builder · Revisions', to: '/enquiries-quotations' },
  { clause: '8.4.2', title: 'Type and extent of control of external providers', description: 'How much control an external process needs, and the verification that it met requirements. Approving a supplier with its insurance or accreditations recorded files this clause.', area: 'Approved Suppliers', to: '/approved-suppliers' },
  { clause: '8.4.3', title: 'Information for external providers', description: 'Sending a purchase order to its supplier files this clause: the order, the supplier and the requirements stated on it. An order downloaded and sent by hand files nothing.', area: 'Stock Management · Purchase orders', to: '/stock' },
  { clause: '8.5', title: 'Production and service provision', description: 'Evidenced by its parts, 8.5.1 to 8.5.6: the work released and controlled, parts traced, customer property filed, work ready to leave, issues after handover, and changes approved.', area: 'Your operational vertical · Jobs', to: null },
  { clause: '8.5.2', title: 'Identification and traceability', description: 'Confirming a job\'s parts as picked files this clause: the job and the parts issued to it from stock. Traced by part number; stock carries no serial or batch number.', area: 'Stock Management · Jobs', to: '/stock?tab=jobs' },
  { clause: '8.5.3', title: 'Property belonging to customers or external providers', description: 'Filing a document as the customer\'s or a supplier\'s, on any record, files this clause: the document, whose it is and where it was filed. Physical property, and a report of loss or damage, are not recorded yet.', area: 'Linked Documents, on any record · Document Register', to: '/admin/documents' },
  { clause: '8.5.4', title: 'Preservation', description: 'Moving a job to the stage its sector marks as ready for departure files this clause: the work finished, protected and waiting to leave.', area: 'Your operational vertical · Jobs', to: null },
  { clause: '8.5.5', title: 'Post-delivery activities', description: 'Closing an issue after handover on a completed job — a call-back, defect, warranty repair or service visit — files this clause with what was done.', area: 'Your operational vertical · Jobs', to: null },
  { clause: '8.5.6', title: 'Control of changes', description: 'Approving a change to a job files this clause: the job, what changed, the effect on its value, who asked for it and who approved it. Raising or rejecting a change files nothing.', area: 'Standard Sales & Jobs · Jobs · Changes', to: '/standard-sales?tab=jobs' },
].sort(byClauseNumber);

/* ISO 14001:2015 EMS: every clause of the standard, in its own order. Each
 * row says what files it; a clause every standard shares is filed by the one
 * record for each standard held (standardsForClause). */
const ISO_14001_CLAUSE_MAP: ClauseMapRow[] = [
  { clause: '4', title: 'Context of the organisation', description: 'Set out in clause 4 of the integrated Business Management System: approving an issue that answers any part of it files this clause. Finalising a Context review files it too.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.1', title: 'Understanding the organisation and its context', description: 'The legislation the business has identified and works to, listed in 4.1 of the Business Management System from the Legal Register: approving an issue that lists it files this clause. Finalising a Context review with its internal and external issues recorded files it too.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.2', title: 'Needs and expectations of interested parties', description: 'The interested parties and their requirements, listed in 4.2 of the Business Management System from the latest finalised Context review: approving an issue that lists them files this clause, and so does finalising a Context review with them recorded.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.3', title: 'Determining the scope of the environmental management system', description: 'The scope of registration in the Business Management System: approving an issue with it filled in on BMS Manual files this clause; with that box empty it files nothing.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '4.4', title: 'Environmental management system and its processes', description: 'Part 2 of 4.4 in the Business Management System, the environmental processes and how they interact: approving an issue with it filled in on BMS Manual files this clause; with that box empty it files nothing.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '5.1', title: 'Leadership and commitment', description: 'Top management taking accountability for the environmental management system. Finalising a management review chaired by top management that decided something (outputs written or an action raised) files this clause, for every standard held.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '5.2', title: 'Environmental policy', description: 'Publishing the environmental policy, or an integrated quality and environmental policy, in the Company Policy Library files this clause when its legal basis names ISO 14001 and its register entry carries 5.2: published is in use. A policy naming only ISO 9001 is the quality policy and files nothing here.', area: 'HR & People · Policies', to: '/hr?tab=policies' },
  { clause: '5.3', title: 'Organisational roles, responsibilities and authorities', description: 'Who is responsible for what, assigned and communicated. Publishing the organisation chart files this clause, for every standard held.', area: 'Administration · Organisation Chart', to: '/admin/org-chart' },
  { clause: '6.1', title: 'Actions to address risks and opportunities', description: 'Reviewing an entry on the Risks & Opportunities register files this clause, for every standard held, and so does approving a risk assessment.', area: 'Accreditations · Risks & Opportunities · Health & Safety', to: '/accreditations?tab=iso&isoSub=risks' },
  { clause: '6.1.2', title: 'Environmental aspects', description: 'The Aspects & Impacts register: each aspect and its impact in normal, abnormal and emergency conditions, its score, whether it is significant, and its control. Recording a review of an aspect files this clause.', area: 'Accreditations · Aspects & Impacts', to: '/accreditations?tab=iso&isoSub=aspects' },
  { clause: '6.1.3', title: 'Compliance obligations (legal register)', description: 'The Legal Register entries that serve the environment, and the aspects each one applies to. Recording an evaluation of compliance with one files this clause.', area: 'Accreditations · Legal Register', to: '/accreditations?tab=iso&isoSub=legalregister' },
  { clause: '6.1.4', title: 'Planning action', description: 'The actions planned for significant aspects, compliance obligations and risks, held with each entry on Aspects & Impacts, the Legal Register and Risks & Opportunities. Their reviews file 6.1.2, 6.1.3 and 6.1; nothing files this clause on its own.', area: 'Accreditations · Aspects & Impacts', to: '/accreditations?tab=iso&isoSub=aspects' },
  { clause: '6.2', title: 'Environmental objectives and planning to achieve them', description: 'The objectives on the ISO 14001 card of the Objectives page, each with its plan. Recording how one was communicated (a finalised meeting, an approved document, or a published memo or policy for staff to sign) files this clause; a quality objective files ISO 9001 6.2 instead.', area: 'Accreditations · Objectives', to: '/accreditations?tab=iso&isoSub=objectives' },
  { clause: '7.1', title: 'Resources', description: 'The resources the environmental management system needs. Approving a document on the Document Register whose clause is 7.1 files this clause; nothing else files it yet.', area: 'Document Register', to: '/admin/documents' },
  { clause: '7.2', title: 'Competence', description: 'An employee signing a toolbox talk (on the Driver Tablet, or by name at a shared screen), or a talk delivered with its attendees, files this clause, for every standard held.', area: 'Training Records · Toolbox Talks · Driver Tablet', to: '/training' },
  { clause: '7.3', title: 'Awareness', description: 'Signing a policy, completing an awareness course with its certificate, or attending a delivered toolbox talk files this clause against the employee, for every standard held.', area: 'HR & People · Policies · Training & Certificates', to: '/hr?tab=policies' },
  { clause: '7.4', title: 'Communication', description: 'Internal and external communication about the management system. Holding a meeting files this clause, and so does sending out a memo, for every standard held.', area: 'Accreditations · Communication · Memos', to: '/accreditations?tab=iso&isoSub=communication' },
  { clause: '7.5', title: 'Documented information', description: 'Controlled documents on the Document Register. Approving a document whose clause is 7.5 files this clause, for every standard held; 7.5.1 to 7.5.3 are filed by their own steps.', area: 'Document Register', to: '/admin/documents' },
  { clause: '7.5.1', title: 'Documented information — general', description: 'Approving an issue of the integrated Business Management System files this clause: it is the documented information the standard requires.', area: 'Accreditations · Business Management System', to: '/accreditations?tab=iso&isoSub=qmsprofile' },
  { clause: '7.5.2', title: 'Creating and updating', description: 'Approving a version on the Document Register files this clause, for every standard held: the number, title, issue and approver a controlled document carries.', area: 'Document Register', to: '/admin/documents' },
  { clause: '7.5.3', title: 'Control of documented information', description: 'Withdrawing a document from use on the Document Register files this clause, for every standard held.', area: 'Document Register', to: '/admin/documents' },
  { clause: '8.1', title: 'Operational planning and control', description: 'Registering a hazardous substance (COSHH) and approving a waste transfer note file this clause. A job moved into work is ISO 9001 8.1 only: it is the work released, not environmental control.', area: 'Health & Safety', to: '/hs' },
  { clause: '8.2', title: 'Emergency preparedness & response', description: 'Emergency Preparedness: the company\'s Emergency Preparedness and Response Procedure (publishing it files this clause), the emergency situations identified on Aspects & Impacts, and the testing of the response. Recording a fire drill or real evacuation on the Fire Register, a test in the emergency response test log, or a check of a spill kit on the first aid register, files this clause.', area: 'Accreditations · Emergency Preparedness', to: '/accreditations?tab=iso&isoSub=emergency' },
  { clause: '9.1', title: 'Monitoring, measurement, analysis and evaluation', description: 'Evidenced by its parts: monitoring environmental performance (9.1.1, Environmental Figures) and evaluation of compliance (9.1.2, Legal Register).', area: 'Accreditations · Environmental Figures · Legal Register', to: '/accreditations?tab=iso&isoSub=environment' },
  { clause: '9.1.1', title: 'Monitoring and measuring environmental performance', description: 'Environmental Figures: electricity, gas and water per building each month, fuel from the fleet fuel logs, waste from the waste transfer notes, cost and CO2e against last year and the targets with each projected for the year at the rate so far, and the meters with their calibration. Marking an ended quarter Reviewed files this clause.', area: 'Accreditations · Environmental Figures', to: '/accreditations?tab=iso&isoSub=environment' },
  { clause: '9.1.2', title: 'Evaluation of compliance', description: 'Each evaluation of compliance with a Legal Register entry that serves the environment: the date, the result and what was checked, and when the next is due. Recording one files this clause.', area: 'Accreditations · Legal Register', to: '/accreditations?tab=iso&isoSub=legalregister' },
  { clause: '9.2', title: 'Internal audit', description: 'One internal-audit programme for the whole management system. Completing an audit, or closing a year\'s programme, files this clause, for every standard held.', area: 'Accreditations · Internal Audit', to: '/accreditations' },
  { clause: '9.3', title: 'Management review', description: 'One management review for the whole management system. Finalising a review files this clause, for every standard held.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '10.1', title: 'Improvement — general', description: 'Opportunities for improvement determined at the management review: finalising a review that recorded outputs or raised actions files this clause, for every standard held.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '10.2', title: 'Nonconformity and corrective action', description: 'Closing a corrective action on the Non-Conformance & Corrective Action register, with its root cause and effectiveness review, files this clause, for every standard held.', area: 'Accreditations · Corrective Actions', to: '/accreditations' },
  { clause: '10.3', title: 'Continual improvement', description: 'Continual improvement decided at the management review: finalising a review that recorded outputs or raised actions files this clause, for every standard held.', area: 'Accreditations · Management Review', to: '/accreditations' },
];

/* ISO 27001:2022 ISMS — clauses 4-10 plus the four Annex A control
 * themes (A.5 Organizational, A.6 People, A.7 Physical, A.8
 * Technological) as grouped rows. No module files iso_27001 evidence
 * yet, so every row reads "Not yet evidenced" — this is the auditor
 * register + roadmap, deliberately honest. */
const ISO_27001_CLAUSE_MAP: ClauseMapRow[] = [
  { clause: '4', title: 'Context of the organisation', description: 'ISMS internal/external issues and interested-party requirements, captured in the Context review.', area: 'Accreditations · Context', to: '/accreditations' },
  { clause: '5.2', title: 'Information security policy', description: 'The signed information-security commitment within the integrated management policy.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '6.1', title: 'Risk assessment & treatment (SoA)', description: 'Information-security risk assessment, treatment plan and Statement of Applicability.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '6.2', title: 'Information security objectives', description: 'Security objectives with their plan and monitoring, in the Objectives register.', area: 'Accreditations · Objectives', to: '/accreditations' },
  { clause: '7.2', title: 'Competence', description: 'Security competence evidenced via qualifications and the Compliance Gate.', area: 'Training Records · Compliance Gate', to: '/training' },
  { clause: '7.3', title: 'Awareness', description: 'Information-security awareness training and policy acknowledgements per employee.', area: 'Training Records', to: '/training' },
  { clause: '7.5', title: 'Documented information', description: 'Controlled ISMS documents, versions and distribution.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '8.1', title: 'Operational planning & control', description: 'Operational security processes carried out per the risk treatment plan.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '9.1', title: 'Monitoring, measurement, analysis & evaluation', description: 'Security KPIs and control effectiveness surfaced on the dashboard.', area: 'Dashboard', to: '/dashboard' },
  { clause: '9.2', title: 'Internal audit', description: 'ISMS internal-audit programme, audits and findings in the Internal Audit module.', area: 'Accreditations · Internal Audit', to: '/accreditations' },
  { clause: '9.3', title: 'Management review', description: 'Annual ISMS management review and its actions in the Management Review module.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '10.2', title: 'Nonconformity & corrective action', description: 'Security nonconformities, root cause and corrective actions in the Corrective Actions (CAPA) register.', area: 'Accreditations · Corrective Actions', to: '/accreditations' },
  { clause: 'A.5', title: 'Annex A.5 — Organizational controls', description: 'Policies, supplier/agreement security, access management, classification — recorded in the SoA.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: 'A.6', title: 'Annex A.6 — People controls', description: 'Screening, terms of employment, disciplinary process and the leaver/offboarding process.', area: 'HR & People', to: '/hr' },
  { clause: 'A.7', title: 'Annex A.7 — Physical controls', description: 'Secure areas, equipment security and clear-desk/site physical security.', area: 'Building Maintenance', to: '/building-maintenance' },
  { clause: 'A.8', title: 'Annex A.8 — Technological controls', description: 'Access control, cryptography (field encryption at rest), logging/monitoring (audit log), backup and device-token control (kiosk).', area: 'Accreditations · ISO Documents', to: '/accreditations' },
];

/* ISO 45001:2018 OH&S — best-evidenced of the four: health_safety
 * already files iso_45001 at clauses 6.1, 6.1.2, 7.3, 8.1, 10.2, so
 * those light up Evidenced from the live store. clause keys match the
 * emitted values exactly. */
const ISO_45001_CLAUSE_MAP: ClauseMapRow[] = [
  { clause: '4', title: 'Context of the organisation', description: 'OH&S internal/external issues and interested-party (incl. workers) requirements, captured in the Context review.', area: 'Accreditations · Context', to: '/accreditations' },
  { clause: '5.2', title: 'OH&S policy', description: 'The signed occupational health & safety commitment within the integrated management policy.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '5.4', title: 'Consultation & participation of workers', description: 'Worker consultation, toolbox talks and safety participation records.', area: 'Health & Safety', to: '/hs' },
  { clause: '6.1', title: 'Actions to address risks & opportunities', description: 'Approved OH&S risk assessments and mitigations — filed automatically.', area: 'Health & Safety', to: '/hs' },
  { clause: '6.1.2', title: 'Hazard identification & assessment of risks', description: 'Hazard identification and risk evaluation captured through the H&S assessment workflow.', area: 'Health & Safety', to: '/hs' },
  { clause: '6.2', title: 'OH&S objectives & planning', description: 'OH&S objectives with their plan and monitoring, in the Objectives register.', area: 'Accreditations · Objectives', to: '/accreditations' },
  { clause: '7.2', title: 'Competence', description: 'OH&S competence evidenced via qualifications and the Compliance Gate.', area: 'Training Records · Compliance Gate', to: '/training' },
  { clause: '7.3', title: 'Awareness', description: 'Safety awareness and toolbox-talk attendance recorded per worker — filed automatically.', area: 'Training Records · Health & Safety', to: '/training' },
  { clause: '7.5', title: 'Documented information', description: 'Controlled OH&S documents, versions and distribution.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
  { clause: '8.1', title: 'Operational planning & control', description: 'Operational H&S controls (COSHH, permits, safe systems of work) — filed automatically.', area: 'Health & Safety', to: '/hs' },
  { clause: '8.2', title: 'Emergency preparedness & response', description: 'Emergency procedures, the live fire-evacuation register, and the fire drill register on the Fire Register tab: each drill or real evacuation with its roll-call, time to clear, what went wrong and the actions. Recording one files this clause, and so does a test in the emergency response test log for a company holding ISO 14001 as well.', area: 'Health & Safety · Fire Register', to: '/hs' },
  { clause: '9.1', title: 'Monitoring, measurement, analysis & performance evaluation', description: 'OH&S KPIs and performance evaluation surfaced on the dashboard.', area: 'Dashboard', to: '/dashboard' },
  { clause: '9.2', title: 'Internal audit', description: 'OH&S internal-audit programme, audits and findings in the Internal Audit module.', area: 'Accreditations · Internal Audit', to: '/accreditations' },
  { clause: '9.3', title: 'Management review', description: 'Annual OH&S management review and its actions in the Management Review module.', area: 'Accreditations · Management Review', to: '/accreditations' },
  { clause: '10.2', title: 'Incident, nonconformity & corrective action', description: 'Corrective actions, root cause and effectiveness review in the Corrective Actions (CAPA) register; H&S incidents/near-misses also feed this clause automatically.', area: 'Accreditations · Corrective Actions', to: '/accreditations' },
  { clause: '10.3', title: 'Continual improvement', description: 'Improvement actions tracked through corrective-action and management-review records.', area: 'Accreditations · ISO Documents', to: '/accreditations' },
];

/** Per-standard clause maps — all four standards populated (Shane's
 *  one-at-a-time sequence: 9001 -> 14001 -> 27001 -> 45001, done). */
export const CLAUSE_MAP_BY_STANDARD: Partial<Record<IsoStandard, ClauseMapRow[]>> = {
  iso_9001: ISO_9001_CLAUSE_MAP,
  iso_14001: ISO_14001_CLAUSE_MAP,
  iso_27001: ISO_27001_CLAUSE_MAP,
  iso_45001: ISO_45001_CLAUSE_MAP,
};

export interface Clause {
  num: string;
  title: string;
  freq: string;
  requires: string;
  must: string[];
  auditor: string;
}

export const GUIDANCE_DATA: Record<
  IsoStandard,
  {
    icon: string;
    title: string;
    facts: Array<{ label: string; value: string }>;
    overview: string[];
    clauses: Clause[];
  }
> = {
  iso_9001: {
    icon: '🏆',
    title: 'ISO 9001:2015 — Quality Management System',
    facts: [
      { label: 'Certification cycle', value: '3 years' },
      { label: 'Surveillance audits', value: 'Year 1 and Year 2' },
      { label: 'Recertification audit', value: 'Every 3 years' },
    ],
    overview: [
      "ISO 9001 is the world's most widely recognised quality management standard. It does not tell you how to run your business — it tells you to document how you run your business, do what your documents say, and demonstrate that you check and improve. That is the entire principle.",
      'Maintaining certification means three things: keeping your documentation current, collecting evidence that your processes are working, and conducting a management review and internal audit every year. Your certifying body will visit for a surveillance audit in year one and year two, then a full recertification audit in year three.',
      'The most common reasons businesses lose ISO 9001 certification are: letting their quality manual go out of date, failing to record management reviews, not closing off corrective actions, and not completing an internal audit before the surveillance visit. All of these are avoidable.',
    ],
    clauses: [
      {
        num: 'Cl. 4.3',
        title: 'Scope of the QMS',
        freq: 'Review: when business changes',
        requires:
          'A documented statement of what your Quality Management System covers — which products, services, sites and activities are in scope and which are excluded, with reasons for any exclusions.',
        must: [
          'Written scope statement in your Business Management System',
          'Justification for any exclusions from the standard',
          'Scope reviewed when the business changes significantly',
        ],
        auditor:
          'That your scope is realistic and that activities you claim are in scope are actually controlled by your QMS. They will look for inconsistencies between your scope statement and how the business actually operates.',
      },
      {
        num: 'Cl. 5.2',
        title: 'Quality Policy',
        freq: 'Review: annually',
        requires:
          'A documented quality policy signed by top management. It must commit to meeting requirements and to continual improvement. It must be communicated to all employees.',
        must: [
          'Signed quality policy document — signed by the director',
          'Evidence it has been communicated to all staff (training record or signed acknowledgement)',
          'Policy reviewed at least annually — date of last review on the document',
        ],
        auditor:
          'That the policy is signed, dated, current and that staff are aware of it. They may ask employees what the quality policy says — if no one knows, that is a finding.',
      },
      {
        num: 'Cl. 6.2',
        title: 'Quality Objectives and Targets',
        freq: 'Review: annually',
        requires:
          'Documented, measurable quality objectives at relevant functions and levels. You must be able to show you monitor progress against them and take action when they are not met.',
        must: [
          'Written objectives register — specific, measurable targets (e.g. fewer than 3 complaints per quarter)',
          'Regular monitoring records — showing actual performance against each target',
          'Actions taken when objectives are not met',
        ],
        auditor:
          'That objectives are measurable, that you actually measure them and that you act on the results. Vague objectives like "improve quality" with no measurement will be raised as a finding.',
      },
      {
        num: 'Cl. 7.1.5',
        title: 'Calibration of Measuring Equipment',
        freq: 'Review: per equipment schedule',
        requires:
          'Any measuring equipment used to verify product or service quality must be calibrated against traceable national or international standards. Records of calibration must be kept.',
        must: [
          'Register of all measuring equipment used for quality verification',
          'UKAS-traceable calibration certificates for each item',
          'Calibration intervals defined and followed',
          'Out-of-calibration equipment quarantined immediately',
        ],
        auditor:
          'That calibration certificates are current, UKAS-traceable and cover the equipment actually in use. They will cross-reference your equipment register with your certificates — missing items will be raised.',
      },
      {
        num: 'Cl. 7.2',
        title: 'Competence',
        freq: 'Review: on role change / qualification expiry',
        requires:
          'You must determine the competence required for each role, ensure people have that competence (through education, training or experience) and keep records as evidence.',
        must: [
          'Training records for all employees — JeanieIQ Training Records module handles this automatically',
          'Qualification certificates on file with expiry dates tracked',
          'Compliance Gate configured to enforce qualification requirements at clock-in',
        ],
        auditor:
          'Training records for a sample of employees, particularly those in roles with specific qualification requirements. Any gap between required and actual competence — and no corrective action — will be a major finding.',
      },
      {
        num: 'Cl. 8.4',
        title: 'Control of Externally Provided Processes (Suppliers)',
        freq: 'Review: annually per supplier',
        requires:
          'You must control externally provided products, services and processes that affect your quality. This means evaluating, selecting and monitoring your suppliers and keeping records of how you do it.',
        must: [
          'Approved Suppliers register — JeanieIQ Approved Suppliers module handles this',
          'Annual supplier evaluation records for all approved suppliers',
          'Criteria for supplier approval and records of how each was assessed',
        ],
        auditor:
          'Your supplier register and evaluation records. They will look for evidence that poor-performing suppliers are addressed and that new suppliers are properly vetted before use.',
      },
      {
        num: 'Cl. 8.7',
        title: 'Control of Nonconforming Outputs (Complaints)',
        freq: 'Review: on each occurrence',
        requires:
          'When a product or service does not meet requirements — including customer complaints — you must identify it, control it, determine what to do and keep records. If the same problem recurs, you must understand why and take corrective action.',
        must: [
          'Customer complaint log with dates, descriptions and outcomes',
          'Root cause analysis (RCA) completed for every closed complaint',
          'Evidence that corrective actions have been taken and are effective',
        ],
        auditor:
          'Your complaint records and whether RCA has been completed. Closed complaints with no RCA on record are one of the most common findings at surveillance audits and will always be raised.',
      },
      {
        num: 'Cl. 9.2',
        title: 'Internal Audit',
        freq: 'At least annually',
        requires:
          'You must conduct planned internal audits of your QMS at regular intervals to determine whether it conforms to the standard and is effectively implemented and maintained.',
        must: [
          'Written internal audit schedule — planned in advance, not reactive',
          'Internal audit report covering all clauses — findings and observations recorded',
          'Corrective actions raised for any findings — with closure dates',
          'Audit completed before each surveillance visit from your certifying body',
        ],
        auditor:
          'Your internal audit report and whether findings have been closed off. Not having completed an internal audit before their visit is a major nonconformity and can result in your certification being suspended.',
      },
      {
        num: 'Cl. 9.3',
        title: 'Management Review',
        freq: 'At least annually',
        requires:
          'Top management must review the QMS at planned intervals to ensure it remains suitable, adequate, effective and aligned with the strategic direction of the organisation.',
        must: [
          'Written management review record — who attended, what was reviewed, decisions made, actions agreed',
          'Inputs must include: audit results, customer feedback, objectives performance, complaints, corrective actions',
          'Outputs must include: opportunities for improvement, resources needed, QMS changes required',
        ],
        auditor:
          'That a management review has taken place, that the right inputs were considered and that actions from the previous review have been completed. A management review that is clearly just a form-filling exercise with no genuine discussion will be challenged.',
      },
      {
        num: 'Cl. 10.2',
        title: 'Corrective Action',
        freq: 'On each nonconformity',
        requires:
          'When a nonconformity occurs — including complaints, audit findings or process failures — you must react, determine root cause, implement a correction and verify effectiveness.',
        must: [
          'Corrective action register — all nonconformities logged with reference numbers',
          'Root cause analysis for each item',
          'Action taken, responsible person, target date and closure date',
          'Evidence that the action was effective — did the problem recur?',
        ],
        auditor:
          'That your corrective actions are genuine — that root cause analysis goes beyond the symptom and that actions have actually been implemented. Open corrective actions with no progress will be raised as findings.',
      },
      /*
       * ── THE REST OF ISO 9001, DRAFTED 2026-08-30 ────────────────────────
       *
       * Shane: *"we need to look at plain english guidance in all areas of ISO
       * so people can understand whta they need to do.. the ISO explinaitions
       * and way of wording are not easy for people to understand that dont do
       * this every day"*.
       *
       * Ten of the fifty-three clauses had guidance; forty-three did not, so
       * most of the standard offered a reader nothing but its own title. These
       * fill that in, in the voice of the ten already here.
       *
       * WHO THIS IS WRITTEN FOR: somebody running a business who does ISO once a
       * year under duress, not a quality manager. So `requires` says what the
       * clause is actually asking for, `must` is what you need to be able to put
       * your hand on, and `auditor` is what gets checked on the day.
       *
       * NOT YET REVIEWED BY THE ISO CONTENT OWNER. RULE-25 — these are
       * statements about what a standard requires, and a confident wrong one
       * sends somebody into an audit having done the wrong thing. Read them
       * before they are trusted; correct freely, they are drafts not rulings.
       */
      {
        num: 'Cl. 4',
        title: 'Context of the organisation',
        freq: 'Review: annually',
        requires:
          'The whole of clause 4 asks one question — do you understand the business you are running a quality system for? Who you are, what could affect you, who cares about the outcome, and which processes deliver it.',
        must: [
          'The four parts covered: 4.1 context, 4.2 interested parties, 4.3 scope, 4.4 processes',
          'Reviewed when the business changes, not only when the auditor is due',
        ],
        auditor:
          'That this is a real picture of your business rather than a form filled in once. Inconsistencies between what you have written and what they see on the day are where findings come from.',
      },
      {
        num: 'Cl. 4.1',
        title: 'Understanding the organisation and its context',
        freq: 'Review: annually',
        requires:
          'A record of the things outside and inside the business that could affect whether you deliver what you promise. Outside: your market, your sector, legislation, the economy. Inside: your people, your equipment, your systems.',
        must: [
          'A written record of internal and external issues — a table is fine, it does not need to be elaborate',
          'Evidence it has been reviewed, with a date',
          'A visible link to the risks you decided to act on under 6.1',
        ],
        auditor:
          'That the issues you list are recognisably yours. Generic text lifted from a template is obvious, and they will ask how a listed issue affects the business — be able to answer.',
      },
      {
        num: 'Cl. 4.2',
        title: 'Needs and expectations of interested parties',
        freq: 'Review: annually',
        requires:
          'A list of everybody with a stake in what you do — customers, employees, regulators, suppliers, shareholders, sometimes neighbours — and what each of them needs from you.',
        must: [
          'A list of interested parties with their requirements against each',
          'Which of those requirements you have taken on as your own obligations',
          'Reviewed and dated',
        ],
        auditor:
          'That you have not stopped at customers. Regulators and employees are interested parties too, and leaving them out is a common finding.',
      },
      {
        num: 'Cl. 4.4',
        title: 'The management system and its processes',
        freq: 'Review: annually',
        requires:
          'You need to know what your processes are, what goes into each, what comes out, who owns it, and how you know it is working. This is the clause that asks you to see the business as a set of processes rather than a set of departments.',
        must: [
          'Your processes identified — a process map or a list, either is acceptable',
          'Inputs, outputs and an owner for each',
          'How each process is measured, and what the measure has to reach',
          'How the processes hand over to each other',
        ],
        auditor:
          'That the processes on paper are the processes people actually follow. They will pick one and walk it end to end with the person who does it.',
      },
      {
        num: 'Cl. 5.1',
        title: 'Leadership & commitment',
        freq: 'Review: at management review',
        requires:
          'Top management has to be visibly involved and accountable — not delegate the quality system to one person and step away. The standard is explicit that leadership cannot be handed off.',
        must: [
          'Directors attending and chairing management reviews — minuted with names',
          'Quality objectives set and resourced by leadership',
          'Evidence that leadership acts on quality issues — decisions, spend, people',
        ],
        auditor:
          'They will interview a director. If the director cannot describe the quality policy, the objectives or the current issues, that is a finding however good the paperwork is.',
      },
      {
        num: 'Cl. 5.1.2',
        title: 'Customer focus',
        freq: 'Review: at management review',
        requires:
          'Leadership has to make sure customer requirements are understood and met consistently, that the risks to that are considered, and that customer satisfaction is actually tracked.',
        must: [
          'Customer requirements captured before work is accepted',
          'On-time and right-first-time performance measured',
          'Complaints and satisfaction reviewed by leadership, not only logged',
        ],
        auditor:
          'That customer feedback reaches the top of the business and changes something. A complaints log nobody has acted on is worse than none.',
      },
      {
        num: 'Cl. 5.3',
        title: 'Roles, responsibilities and authorities',
        freq: 'Review: when people or structure change',
        requires:
          'Everyone needs to know what they are responsible for and what they are allowed to decide. Assigned by top management and communicated — not assumed.',
        must: [
          'An organisation chart that is current',
          'Responsibilities written down — job descriptions or a responsibility matrix',
          'Who is authorised to do what: approve documents, release work, stop a job',
          'People able to say what they are responsible for',
        ],
        auditor:
          'They will ask someone on the floor who they report to and what they are allowed to decide. A chart nobody recognises is a finding.',
      },
      {
        num: 'Cl. 6.1',
        title: 'Actions to address risks & opportunities',
        freq: 'Review: annually',
        requires:
          'Having understood your context and your interested parties, you have to decide what could go wrong, what could go right, and do something about both. It does not demand a formal risk methodology — it demands that you thought about it and acted.',
        must: [
          'A risk register covering the issues from 4.1 and 4.2',
          'Opportunities recorded as well as risks — not only the bad news',
          'The action decided for each, with an owner',
          'Evidence the actions happened and whether they worked',
        ],
        auditor:
          'That risks connect back to your context and forward to real actions. A register that has not changed in three years says nobody is using it.',
      },
      {
        num: 'Cl. 6.3',
        title: 'Planning of changes',
        freq: 'When a change is planned',
        requires:
          'When you change something that affects the quality system — a new process, a new site, a big new customer, new equipment — it has to be planned rather than improvised, with the consequences and resources thought through.',
        must: [
          'A record of significant changes and why they were made',
          'What was considered: consequences, resources, responsibilities',
          'Who approved it',
        ],
        auditor:
          'They will pick a change that clearly happened — new premises, new machine, new certification — and ask to see it was planned. "It just happened" is a finding.',
      },
      {
        num: 'Cl. 7.1.1',
        title: 'Resources — general',
        freq: 'Review: at management review',
        requires:
          'You have to work out what the quality system needs to run — people, equipment, premises, money — and actually provide it. The sub-clauses that follow break that down.',
        must: [
          'Resource needs identified, usually through management review',
          'Evidence that what was needed was provided, or a decision recorded not to',
        ],
        auditor:
          'That resource shortfalls raised in management review were acted on. A recurring "we need another fitter" with no outcome is a finding against leadership as much as this clause.',
      },
      {
        num: 'Cl. 7.1.2',
        title: 'People',
        freq: 'Review: when the workload or team changes',
        requires:
          'Enough people, and the right people, to run the processes and the quality system. Not the same as competence, which is 7.2 — this is about having sufficient hands.',
        must: [
          'Roles filled, with cover for absence in the critical ones',
          'Evidence you noticed and acted when you were short',
        ],
        auditor:
          'Whether quality problems trace back to being under-resourced. Repeated late delivery blamed on staffing, with nothing done, lands here.',
      },
      {
        num: 'Cl. 7.1.3',
        title: 'Infrastructure',
        freq: 'Review: annually, plus planned maintenance',
        requires:
          'The buildings, equipment, vehicles, hardware and software you need to do the work — provided and maintained so they stay fit for use.',
        must: [
          'An asset or equipment list',
          'Planned maintenance with records of it being done',
          'Breakdown and repair records',
          'Statutory inspections in date — LOLER, PUWER, electrical, vehicles',
        ],
        auditor:
          'They will pick an asset and ask for its maintenance history. An overdue statutory inspection is one of the quickest findings there is.',
      },
      {
        num: 'Cl. 7.1.4',
        title: 'Environment for the operation of processes',
        freq: 'Review: annually',
        requires:
          'The conditions people work in — physical and human. Temperature, noise, light and cleanliness, and equally the social side: workload, stress, behaviour.',
        must: [
          'Workplace conditions assessed for the work being done',
          'Controls where conditions could affect quality',
          'The human side considered, not only the physical',
        ],
        auditor:
          'That you have thought beyond temperature and lighting. Under 9001 this clause explicitly includes psychological and social factors, which most businesses miss.',
      },
      {
        num: 'Cl. 7.1.6',
        title: 'Organisational knowledge',
        freq: 'Review: annually',
        requires:
          'The knowledge the business needs to run — how the work is actually done, the lessons learned, the things one person knows and nobody else does — captured so it survives them leaving.',
        must: [
          'Key knowledge identified, especially where it sits with one person',
          'Written down or trained out — procedures, work instructions, records of lessons learned',
          'How you get new knowledge when you need it',
        ],
        auditor:
          'What happens when your most experienced person leaves. If the answer is that the business would struggle, they will want to see what you are doing about it.',
      },
      {
        num: 'Cl. 7.3',
        title: 'Awareness',
        freq: 'Ongoing, checked at induction and review',
        requires:
          'Your people have to know the quality policy, the objectives that apply to them, what their work contributes, and what happens if the system is not followed. Awareness, not competence.',
        must: [
          'Induction covering the policy and objectives',
          'Policy acknowledgements or toolbox talks recorded per person',
          'Objectives communicated to the people who affect them',
        ],
        auditor:
          'They will ask employees directly. Not word-for-word recall — but blank looks about whether the company has a quality policy at all is a finding.',
      },
      {
        num: 'Cl. 7.4',
        title: 'Communication',
        freq: 'Review: annually',
        requires:
          'You have to decide what needs communicating about the quality system, to whom, when, how, and who does it. Internal and external both.',
        must: [
          'A communication plan or matrix — what, who, when, how',
          'Evidence it happens: meeting minutes, notices, briefings, emails',
          'External communication covered too — customers, suppliers, regulators',
        ],
        auditor:
          'That communication is two-way and planned rather than incidental. "We all talk to each other" is not an answer to this clause.',
      },
      {
        num: 'Cl. 7.5',
        title: 'Documented information',
        freq: 'Review: annually',
        requires:
          'The parent clause for your documents and records. The standard asks for what it requires, plus whatever else you decide you need to run the business properly.',
        must: [
          'A document register listing controlled documents with version and owner',
          'The records the standard requires, retained and retrievable',
        ],
        auditor:
          'That you can find a named document quickly and that the copy in use is the current version. Time spent hunting for it is itself the finding.',
      },
      {
        num: 'Cl. 7.5.1',
        title: 'Documented information — general',
        freq: 'Review: annually',
        requires:
          'Your system has to include the documents the standard demands and the ones you have decided are necessary. What is necessary varies with the size and complexity of the business — a small firm is not expected to match a large one.',
        must: [
          'The documents the standard requires: scope, policy, objectives',
          'Your own procedures where their absence would cause problems',
        ],
        auditor:
          'That the documentation fits the business. Over-documentation is as much a problem as too little, because nobody follows it.',
      },
      {
        num: 'Cl. 7.5.2',
        title: 'Creating and updating',
        freq: 'On every document change',
        requires:
          'When a document is created or changed it needs identifying and describing — title, date, reference — in a suitable format, and it must be reviewed and approved before use.',
        must: [
          'Every controlled document carrying a title, reference, date and version',
          'Evidence of review and approval before issue — a name and a date',
        ],
        auditor:
          'They will open a document and look for the approval. An unapproved document in use is a finding whatever its content.',
      },
      {
        num: 'Cl. 7.5.3',
        title: 'Control of documented information',
        freq: 'Ongoing',
        requires:
          'Documents must be available where they are needed and protected — from loss, from unauthorised change, from being read by people who should not. Superseded versions must not be in use by mistake.',
        must: [
          'Version control, with superseded versions clearly marked or withdrawn',
          'Backups, and access control on anything confidential',
          'Retention periods set, and old records disposed of properly',
          'External documents you rely on identified and controlled',
        ],
        auditor:
          'They will look for an out-of-date version still on a wall or a desk. It is the most common finding against this clause by a distance.',
      },
      {
        num: 'Cl. 8.1',
        title: 'Operational planning & control',
        freq: 'Per job or contract',
        requires:
          'Plan and control the work that delivers your product or service: what the requirements are, what resources it needs, what you check, and what records prove it was done.',
        must: [
          'Job or contract planning records',
          'The acceptance criteria the work is checked against',
          'Records that the checks happened',
          'Control over anything outsourced',
        ],
        auditor:
          'They will follow a real job from order to delivery and expect the planned controls to have left records at each step.',
      },
      {
        num: 'Cl. 8.2',
        title: 'Requirements for products and services',
        freq: 'Per enquiry or order',
        requires:
          'The parent clause for dealing with customers — talking to them, working out what they want, checking you can do it, and handling changes.',
        must: [
          'The sub-clauses covered: 8.2.1 communication, 8.2.2 determining, 8.2.3 review, 8.2.4 changes',
        ],
        auditor:
          'That you can show the thread from enquiry to order to delivery for a real customer.',
      },
      {
        num: 'Cl. 8.2.1',
        title: 'Customer communication',
        freq: 'Ongoing',
        requires:
          'How you communicate with customers — about what you offer, enquiries and orders, their feedback and complaints, and their property if you hold any.',
        must: [
          'Records of enquiries and quotations',
          'Order confirmations',
          'A complaints route the customer knows about',
          'Contingency arrangements agreed where relevant',
        ],
        auditor:
          'That customers get a clear route to complain and that complaints are recorded when they arrive by phone or in person, not only in writing.',
      },
      {
        num: 'Cl. 8.2.2',
        title: 'Determining the requirements',
        freq: 'Per enquiry',
        requires:
          'Before you commit, establish what is actually being asked for — including the legal and regulatory requirements that apply, whether or not the customer mentioned them — and be sure you can meet what you claim.',
        must: [
          'Requirements captured in writing before quoting',
          'Applicable legislation and standards identified',
          'Anything you add yourself recorded too',
        ],
        auditor:
          'That statutory requirements are picked up even when the customer did not ask. Missing them is where this clause bites.',
      },
      {
        num: 'Cl. 8.2.3',
        title: 'Review of the requirements for products & services',
        freq: 'Per order, before acceptance',
        requires:
          'Before accepting an order, review that you can actually meet it — that the requirements are defined, differences from the quote are resolved, and you have the capacity and capability.',
        must: [
          'Evidence of review before acceptance — a signature, an approval, a system record',
          'Differences between quote and order resolved and recorded',
          'Verbal orders confirmed back before work starts',
        ],
        auditor:
          'That the review happened BEFORE acceptance rather than being reconstructed afterwards. Dates matter here.',
      },
      {
        num: 'Cl. 8.2.4',
        title: 'Changes to requirements',
        freq: 'Whenever a customer changes the job',
        requires:
          'When a customer changes what they want, the paperwork has to change with it and the people doing the work have to be told.',
        must: [
          'Variations and change orders recorded',
          'Amended documents reissued to whoever is working from them',
          'Agreement from the customer to the change',
        ],
        auditor:
          'They will look for a job that changed and check whether the shop floor was working to the current version. Verbal changes that never reached the paperwork are the classic finding.',
      },
      {
        num: 'Cl. 8.3',
        title: 'Design and development',
        freq: 'Per design project',
        requires:
          'If you design what you deliver, the design has to be planned, its inputs defined, its outputs checked against those inputs, reviewed, verified and validated — with changes controlled. If you build strictly to a customer design you may be able to exclude this, but you must justify the exclusion in your scope.',
        must: [
          'Design plans with stages and responsibilities',
          'Design inputs recorded — requirements, statutory, previous experience',
          'Review, verification and validation records',
          'Design changes controlled and approved',
        ],
        auditor:
          'First, whether you design at all. Many businesses exclude this and should not — if you decide how something is made rather than following a customer drawing, you are designing.',
      },
      /*
       * The three sub-clauses of 8.3 the platform files on its own
       * (2026-10-03). Each is written for the switchboard builder, where the
       * platform's design happens, and reads as what a person does there. A
       * business that excludes 8.3 excludes these with it.
       */
      {
        num: 'Cl. 8.3.3',
        title: 'Design and development inputs',
        freq: 'Per design, before the work',
        requires:
          'Before you design anything, you need to know what it has to meet: what the customer specified, what the law and the standards require, and what you learned last time. That has to be written down, not held in somebody\'s head.',
        must: [
          "The customer's specification and drawings, kept with the design",
          'A record of a person having read them and confirmed what they require',
          'Anything statutory or from a standard that the design must meet',
        ],
        auditor:
          'Pick a design and ask to see what it was designed to. In the platform that is the customer\'s documents in the folder and the extracted spec brief a person confirmed from them.',
      },
      {
        num: 'Cl. 8.3.4',
        title: 'Design and development controls',
        freq: 'Per design approved',
        requires:
          'Somebody has to check that the design does what it was meant to do before it is built, and the check has to be recorded: what was reviewed, when, and who said yes. Where the customer approves the layout, the customer is that somebody.',
        must: [
          'The design the customer approved, at its revision number',
          'Their order against it, and the date',
          'Who recorded the approval',
        ],
        auditor:
          'Pick a built board and ask who approved its design and against which revision. In the platform the win, or the variation order, names the revision the customer ordered, and that is the approval on record.',
      },
      {
        num: 'Cl. 8.3.5',
        title: 'Design and development outputs',
        freq: 'Per revision issued',
        requires:
          'What the design produced has to be kept at a stated issue, so it can be built from, bought against and checked against the inputs. A drawing set with no issue number is not an output anyone can rely on.',
        must: [
          'Drawings, parts lists and the quotation document issued at one revision number',
          'The date each revision was sealed',
          'The previous issues kept, not overwritten',
        ],
        auditor:
          'Ask for the current issue of a design and whether the drawings, parts list and quotation agree on the number. In the platform, saving a revision files the whole set at that number and dates it.',
      },
      {
        num: 'Cl. 8.3.6',
        title: 'Design and development changes',
        freq: 'Per change to a design',
        requires:
          'When a design changes, during the work or after it, the change has to be identified, reviewed and authorised, and the record kept: why it changed, who asked, and who agreed.',
        must: [
          'Each revision opened with the reason, who asked for it and the contact',
          'What changed between one issue and the next',
          "The customer's acceptance where the change moves the price",
        ],
        auditor:
          'Take a design with several revisions and ask why revision three exists. In the platform the revision is declared before the work, with its trigger, so the answer is on the record rather than remembered.',
      },
      {
        num: 'Cl. 8.4.1',
        title: 'External providers — evaluation & selection',
        freq: 'On approval, then reviewed periodically',
        requires:
          'Suppliers and sub-contractors have to be assessed before you use them, against criteria you have set, and re-assessed on how they actually perform.',
        must: [
          'An approved supplier list',
          'The criteria used to approve them',
          'Performance monitored — deliveries, quality, complaints',
          'Records of re-evaluation, and of removing anyone who fails',
        ],
        auditor:
          'They will pick a supplier off a recent invoice and look for them on the approved list. One that is not is an immediate finding.',
      },
      {
        num: 'Cl. 8.4.2',
        title: 'Type and extent of control of external providers',
        freq: 'Review: annually',
        requires:
          'How tightly you control a supplier should match how much their work affects your product. A critical sub-contractor needs more control than a stationery supplier, and you have to be able to say why you chose the control you did.',
        must: [
          'Controls defined per supplier or category — inspection, audit, certification',
          'Goods-in checks where they matter',
          'Outsourced processes identified and controlled as if they were yours',
        ],
        auditor:
          'That outsourcing a process has not outsourced the responsibility. Work done by others still has to meet your requirements and you have to show how you know it does.',
      },
      {
        num: 'Cl. 8.4.3',
        title: 'Information for external providers',
        freq: 'Per order',
        requires:
          'Suppliers must be told clearly what you need — specification, methods, qualification requirements, and how you will verify it — before the work starts.',
        must: [
          'Purchase orders carrying the specification and any standards',
          'Drawings, methods or approval requirements issued with the order',
          'Competence or certification requirements stated where they apply',
        ],
        auditor:
          'Purchase orders that say only "as discussed" or a part number with no specification. If a supplier could not tell what good looks like from your order, that is the finding.',
      },
      {
        num: 'Cl. 8.5',
        title: 'Production and service provision',
        freq: 'Ongoing',
        requires:
          'The parent clause for actually doing the work under controlled conditions, covering identification, customer property, preservation, post-delivery and changes.',
        must: [
          'The sub-clauses covered: 8.5.1 through 8.5.6',
        ],
        auditor:
          'That the controls are visible on the floor rather than only in the manual.',
      },
      {
        num: 'Cl. 8.5.1',
        title: 'Control of production & service provision',
        freq: 'Ongoing',
        requires:
          'The work has to be done under controlled conditions: information available about what is being made, suitable equipment, monitoring at the right points, competent people, and release only when the checks pass.',
        must: [
          'Work instructions or drawings available at the point of work',
          'Calibrated equipment where measurement matters',
          'In-process checks recorded',
          'Competent people doing the work',
        ],
        auditor:
          'They will go and look. Work instructions in an office folder while the floor works from memory is the common gap.',
      },
      {
        num: 'Cl. 8.5.2',
        title: 'Identification and traceability',
        freq: 'Ongoing',
        requires:
          'You have to be able to identify what the work is and, where it matters, trace it back — which batch, which material, which job. And you have to know its inspection status: checked or not.',
        must: [
          'Job or batch identification through the process',
          'Inspection status clear — passed, pending, rejected',
          'Traceability to materials and certificates where required',
        ],
        auditor:
          'They will point at something part-finished and ask what it is and whether it has been checked. If nobody can say, that is the finding.',
      },
      {
        num: 'Cl. 8.5.3',
        title: 'Property belonging to customers or external providers',
        freq: 'While it is in your care',
        requires:
          'Anything belonging to a customer or supplier that is in your hands — materials, tools, drawings, data — has to be identified, protected and safeguarded, and they have to be told if it is lost or damaged.',
        must: [
          'Customer property identified as theirs',
          'Records of what you hold',
          'Reporting to the customer if it is damaged or lost',
          'Customer data and drawings covered, not only physical items',
        ],
        auditor:
          'Most businesses forget that drawings, specifications and data are customer property too. Free-issue material with no record is the other common gap.',
      },
      {
        num: 'Cl. 8.5.4',
        title: 'Preservation',
        freq: 'Ongoing',
        requires:
          'Protect the work through the process and until delivery — handling, packaging, storage, contamination, shelf life.',
        must: [
          'Storage suitable for what is stored',
          'Handling and packaging that prevents damage',
          'Shelf life managed where materials expire',
        ],
        auditor:
          'They will walk the stores. Out-of-date material still in the racks is a finding, and so is damage nobody has noticed.',
      },
      {
        num: 'Cl. 8.5.5',
        title: 'Post-delivery activities',
        freq: 'Per contract',
        requires:
          'Whatever you have committed to after delivery — warranty, servicing, maintenance, disposal — has to be considered and delivered, along with any legal obligations that continue after handover.',
        must: [
          'Warranty and after-sales commitments recorded',
          'Records of post-delivery work carried out',
          'Statutory obligations after delivery identified',
        ],
        auditor:
          'That warranty work is recorded and fed back as improvement information rather than absorbed quietly.',
      },
      {
        num: 'Cl. 8.5.6',
        title: 'Control of changes',
        freq: 'When production or service changes',
        requires:
          'Changes to how the work is done have to be reviewed and controlled so you keep meeting requirements, with a record of what changed, who authorised it and what came of it.',
        must: [
          'Change records for process or method changes',
          'Who reviewed and authorised each',
          'Any actions arising from the change',
        ],
        auditor:
          'They will find a process that clearly changed and look for the record. Improvements made informally with no record fail here even though the change was a good one.',
      },
      {
        num: 'Cl. 8.6',
        title: 'Release of products & services',
        freq: 'Per delivery',
        requires:
          'Nothing goes to the customer until the planned checks have been completed satisfactorily — and the record has to say who authorised release.',
        must: [
          'Final inspection or check records',
          'Named authorisation for release',
          'Evidence that the planned checks were all done',
        ],
        auditor:
          'That release is authorised by someone with the authority to do it, and that early release — when it happens — was approved rather than assumed.',
      },
      {
        num: 'Cl. 9.1',
        title: 'Monitoring, measurement, analysis & evaluation',
        freq: 'Review: at management review',
        requires:
          'You have to decide what to measure, how, when, and when to evaluate the results — then actually do it and act on what it tells you.',
        must: [
          'What is measured, and why, written down',
          'The measurements taken, on the stated frequency',
          'Evaluation of what the numbers mean, not just the numbers',
        ],
        auditor:
          'That measurement leads to action. Data collected and filed with nothing following from it is a finding against this clause.',
      },
      {
        num: 'Cl. 9.1.1',
        title: 'Monitoring & measurement',
        freq: 'Per the plan you set',
        requires:
          'The practical half of 9.1 — what you monitor, the methods used, and doing it often enough for the results to be valid.',
        must: [
          'Monitoring plan with methods and frequency',
          'Results retained as records',
          'Measuring equipment fit for purpose and calibrated where it matters',
        ],
        auditor:
          'Gaps in the record where monitoring was due and did not happen. A missing month is easier to spot than most people expect.',
      },
      {
        num: 'Cl. 9.1.2',
        title: 'Customer satisfaction',
        freq: 'Review: at least annually',
        requires:
          'You must monitor how customers see you — whether their needs have been met. Not necessarily a survey: complaints, repeat business, returns, praise and lost bids all count.',
        must: [
          'A method for gathering customer perception, chosen and recorded',
          'The information actually gathered',
          'Reviewed at management review, with actions where it is poor',
        ],
        auditor:
          'That you have a method and use it. "We would hear if they were unhappy" is not monitoring, and low survey response rates need a second route.',
      },
      {
        num: 'Cl. 9.1.3',
        title: 'Analysis & evaluation',
        freq: 'Review: at management review',
        requires:
          'Analyse the data you have gathered to judge whether the system is working: product conformity, customer satisfaction, supplier performance, the effectiveness of your actions on risk.',
        must: [
          'Analysis presented to management review — trends, not just totals',
          'Conclusions drawn about whether the system is effective',
          'Actions where the analysis says something is wrong',
        ],
        auditor:
          'That the analysis answers the question "is the system working?" rather than reporting activity. A pack of charts with no conclusion is a finding.',
      },
      {
        num: 'Cl. 10.1',
        title: 'Improvement — general',
        freq: 'Ongoing',
        requires:
          'You have to look for opportunities to improve and act on them — improving products and services, correcting and preventing undesired effects, and improving how the system performs.',
        must: [
          'A route for improvement suggestions',
          'Improvements recorded and their effect measured',
          'Evidence of improvement over time',
        ],
        auditor:
          'Improvement beyond fixing problems. Corrective action alone is 10.2; this clause wants evidence you also make things better when nothing is broken.',
      },
      {
        num: 'Cl. 10.3',
        title: 'Continual improvement',
        freq: 'Review: at management review',
        requires:
          'The system as a whole has to keep getting better, using what the analysis, evaluation and management review tell you about where the need is.',
        must: [
          'Improvement actions traceable to management review outputs',
          'Objectives that move over time rather than repeating unchanged',
          'Results of improvements reviewed',
        ],
        auditor:
          'Objectives identical to last year, and a management review whose outputs never become actions. Both say the system is static rather than improving.',
      },
    ],
  },
  iso_14001: {
    icon: '🌿',
    title: 'ISO 14001:2015 — Environmental Management System',
    facts: [
      { label: 'Certification cycle', value: '3 years' },
      { label: 'Surveillance audits', value: 'Year 1 and Year 2' },
      { label: 'Legal obligation', value: 'Environmental legislation compliance' },
    ],
    overview: [
      'ISO 14001 requires you to identify every way your business affects the environment — through your processes, products and services — and to manage those aspects systematically. This means knowing what legislation applies to you, controlling your significant environmental aspects, and demonstrating continual improvement.',
      'What that looks like depends on what you do. A workshop manages its chemicals and waste; an office its energy, paper and travel; a fleet its fuel and emissions. The standard asks you to work out which of your activities matter most to the environment and to control those, not to follow a list written for someone else.',
      'The standard does not require you to have zero environmental impact — it requires you to know what your impacts are, manage them and improve over time. The records your platform keeps as the work happens are the backbone of that evidence.',
    ],
    clauses: [
      {
        num: 'Cl. 4',
        title: 'Context of the organisation',
        freq: 'Review: annually, and when the business changes',
        requires:
          'Before you can manage your environmental impact you have to understand your business in its setting: what is going on inside and outside it, who has an interest in how you affect the environment, what the system covers, and how it works.',
        must: [
          'The issues inside and outside the business that affect the environmental system (4.1)',
          'The interested parties and what they need from you (4.2)',
          'A written scope (4.3) and a description of how the system and its processes fit together (4.4)',
        ],
        auditor:
          'That clause 4 reads like your business and not a template. They will compare it with what they see on the day and ask how recently it was looked at.',
      },
      {
        num: 'Cl. 4.1',
        title: 'Understanding the organisation and its context',
        freq: 'Review: annually, and when the business changes',
        requires:
          'You have to identify the internal and external issues that affect what your environmental system can achieve. That includes environmental conditions that affect you or that you affect — weather, flooding, air quality, the supply of water, fuel and materials — and, since the 2024 amendment, whether climate change is a relevant issue for you.',
        must: [
          'A list of internal and external issues, environmental conditions among them',
          'A recorded decision on whether climate change is relevant, and why',
          'Evidence the list is reviewed — usually at the management review',
        ],
        auditor:
          'That the issues are real and specific to you, and that you have answered the climate change question rather than skipped it. A list of business issues with nothing environmental on it will be challenged.',
      },
      {
        num: 'Cl. 4.2',
        title: 'Needs and expectations of interested parties',
        freq: 'Review: annually',
        requires:
          'You have to know who has an interest in your environmental performance — regulators, neighbours, customers, employees, landlords, insurers — what they need from you, and which of those needs you will treat as compliance obligations. Some will have requirements about climate change.',
        must: [
          'A list of interested parties and their relevant needs and expectations',
          'Which of those needs are compliance obligations (they then go on the legal register, 6.1.3)',
          'Evidence the list is kept up to date',
        ],
        auditor:
          'That the regulator and your neighbours are there if you could affect them, and that the needs you have taken on appear in your compliance obligations.',
      },
      {
        num: 'Cl. 4.3',
        title: 'Determining the scope of the environmental management system',
        freq: 'Review: when the business changes',
        requires:
          'You have to write down what the environmental system covers: which sites, activities, products and services. You decide the boundary, but it must include everything you can control or influence that has an environmental effect, and you cannot leave out an activity just because it is awkward.',
        must: [
          'A written scope naming the sites and activities covered',
          'The scope taking account of 4.1, 4.2 and your compliance obligations',
          'The scope available to interested parties',
        ],
        auditor:
          'That the scope matches the certificate and what actually happens. A depot, a yard or a site activity missing from the scope is a common finding.',
      },
      {
        num: 'Cl. 4.4',
        title: 'Environmental management system and its processes',
        freq: 'Review: annually',
        requires:
          'You have to run the environmental system as a set of processes that work together, and keep improving it, using what you learned in 4.1 and 4.2. In an integrated system this is usually one description of the processes, with the environmental part set out beside the quality part.',
        must: [
          'A description of the environmental processes and how they interact',
          'Owners for each process',
          'Evidence the system is maintained and improved, not written once and left',
        ],
        auditor:
          'That the processes described are the ones people follow. They will pick one and walk it from start to finish.',
      },
      {
        num: 'Cl. 5.1',
        title: 'Leadership and commitment',
        freq: 'Ongoing',
        requires:
          'Top management must own the environmental system: take accountability for it, make sure the policy and objectives fit the direction of the business, build the system into everyday work, provide the resources, and support other managers in their part of it.',
        must: [
          'Top management chairing or attending the management review',
          'Environmental objectives agreed by top management',
          'Resources given when the system needs them',
        ],
        auditor:
          'They will interview a director. "The quality manager deals with that" is the answer that produces a finding.',
      },
      {
        num: 'Cl. 5.2',
        title: 'Environmental Policy',
        freq: 'Review: annually',
        requires:
          'A signed environmental policy committing to protecting the environment, including preventing pollution, to meeting your compliance obligations, and to continual improvement. It must suit the nature and scale of your business, be communicated to all staff and be available to interested parties.',
        must: [
          'Signed environmental policy — director signature, current date',
          'Policy communicated to all employees — training record or signed acknowledgement',
          'Policy reviewed annually and updated when the business changes significantly',
        ],
        auditor:
          'That the policy is current, signed, communicated and that it specifically commits to pollution prevention and legal compliance — not just vague environmental statements.',
      },
      {
        num: 'Cl. 5.3',
        title: 'Organisational roles, responsibilities and authorities',
        freq: 'Review: when people or roles change',
        requires:
          'Responsibilities and authorities for the environmental system have to be assigned and communicated, including who makes sure the system meets the standard and who reports on environmental performance to top management.',
        must: [
          'An organisation chart or responsibilities list that includes environmental roles',
          'A named person who reports environmental performance to top management',
          'People knowing what their environmental responsibilities are',
        ],
        auditor:
          'That the people named know they hold the role. They will ask someone what they are responsible for and compare the answer with the chart.',
      },
      {
        num: 'Cl. 6.1',
        title: 'Actions to address risks and opportunities',
        freq: 'Review: annually, and when the business changes',
        requires:
          'You have to identify the risks and opportunities that come from your environmental aspects, your compliance obligations and the issues in 4.1 and 4.2, including the emergencies that could happen, and plan what you will do about them.',
        must: [
          'A risks and opportunities register that covers the environment',
          'The potential emergency situations within your scope identified',
          'Actions planned for each significant risk, and reviewed',
        ],
        auditor:
          'That environmental risks appear on the register alongside the business ones, and that the actions planned were actually taken.',
      },
      {
        num: 'Cl. 6.1.2',
        title: 'Environmental Aspects Register',
        freq: 'Review: annually or on significant change',
        requires:
          'You must identify all the ways your activities, products and services interact with the environment — these are your environmental aspects — in normal, abnormal and emergency conditions, looking across the life of what you provide. You must then decide, using criteria you have set, which are significant, and control those.',
        must: [
          'Environmental aspects register — every activity with its environmental impact identified',
          'Normal, abnormal and emergency conditions considered',
          'The criteria used to decide significance, and the significant aspects marked',
          'Controls in place for all significant aspects, and the register reviewed when activities change',
        ],
        auditor:
          'That your register is comprehensive and realistic — covering all your operations, not just the obvious ones. They will check that your significant aspects have controls in place and that those controls are actually working.',
      },
      {
        num: 'Cl. 6.1.3',
        title: 'Compliance obligations (legal register)',
        freq: 'Review: annually, and when the law changes',
        requires:
          'You must identify the environmental laws and other requirements that apply to you — permits, licences, customer and landlord requirements, codes you have signed up to — have access to them, and know how each one applies to your aspects.',
        must: [
          'A legal register listing every applicable law and other requirement, with its source',
          'How each one applies to you and which aspects it covers',
          'The register reviewed regularly — legislation changes',
          'Evidence of compliance — permits, licences, waste carrier records',
        ],
        auditor:
          'That your legal register is current and complete. Missing relevant legislation — or listing it without evidence of compliance — will be raised. Legislation changes regularly and auditors expect the register to reflect this.',
      },
      {
        num: 'Cl. 6.1.4',
        title: 'Planning action',
        freq: 'Review: annually',
        requires:
          'For your significant aspects, your compliance obligations and your risks and opportunities, you have to plan the action you will take, build it into the way the business works, and decide how you will check it worked. You can take account of the technology available and the cost.',
        must: [
          'An action against each significant aspect, compliance obligation and risk',
          'Each action built into a process, a procedure or an objective — not a separate list',
          'How the effectiveness of each action will be checked',
        ],
        auditor:
          'That the actions on your registers lead somewhere: an objective, a procedure, a control on site. An action that nobody owns and nobody checks is the finding.',
      },
      {
        num: 'Cl. 6.2',
        title: 'Environmental objectives and planning to achieve them',
        freq: 'Review: at least annually',
        requires:
          'You have to set environmental objectives that follow from your policy and significant aspects, are measurable where practical, are monitored and communicated, and have a plan: what will be done, the resources, who is responsible, when, and how the result will be judged.',
        must: [
          'Written environmental objectives with targets',
          'A plan for each: action, resources, owner, date, measure',
          'Progress monitored and objectives communicated to the people who deliver them',
        ],
        auditor:
          'That the objectives relate to your significant aspects and that progress has been measured. An objective with no figure behind it is hard to defend.',
      },
      {
        num: 'Cl. 7.1',
        title: 'Resources',
        freq: 'Review: at the management review',
        requires:
          'You have to provide what the environmental system needs to work and improve: people, equipment, money, time and the infrastructure that controls your aspects, such as bunded storage and spill kits.',
        must: [
          'Resources for the environmental system identified',
          'Resource needs considered at the management review',
          'Environmental controls on site in place and maintained',
        ],
        auditor:
          'That the controls your register relies on exist. A bund that is full of rainwater or a spill kit that is empty undoes the paperwork.',
      },
      {
        num: 'Cl. 7.2',
        title: 'Competence',
        freq: 'Ongoing — reviewed per person',
        requires:
          'People whose work can affect your environmental performance or your ability to meet compliance obligations must be competent, through education, training or experience, and you have to identify the training their environmental aspects call for.',
        must: [
          'The environmental competence each role needs',
          'Training records — courses, toolbox talks, inductions',
          'Action taken where someone is not yet competent, and its effectiveness checked',
        ],
        auditor:
          'They will ask someone who handles waste, chemicals or fuel how they do it and then look for the training record behind the answer.',
      },
      {
        num: 'Cl. 7.3',
        title: 'Awareness',
        freq: 'Ongoing — at induction and when things change',
        requires:
          'Everyone working for you must know the environmental policy, the significant aspects and impacts linked to their own work, how they contribute to the system, and what happens if they do not follow it, including breaching a compliance obligation.',
        must: [
          'Environmental awareness at induction',
          'The policy signed or acknowledged by staff',
          'Toolbox talks or briefings on the significant aspects',
        ],
        auditor:
          'They will ask an employee what the main environmental risks of their job are and what to do in a spillage. A blank look is the finding.',
      },
      {
        num: 'Cl. 7.4',
        title: 'Communication',
        freq: 'Review: annually',
        requires:
          'You have to decide what to communicate about the environmental system, to whom, when and how, inside the business and outside it, including anything your compliance obligations require you to report. What you say must be consistent with your own records, and you must respond to relevant communications from outside.',
        must: [
          'A communication plan — what, who, when, how, internal and external',
          'Evidence internal communication happens: meetings, briefings, notices',
          'External communication handled and recorded — regulators, neighbours, complaints',
        ],
        auditor:
          'How you would handle a complaint from a neighbour or an enquiry from the regulator, and the records of the last one.',
      },
      {
        num: 'Cl. 7.5',
        title: 'Documented information',
        freq: 'Review: annually',
        requires:
          'You must keep the documents and records the standard requires and the ones you need for the system to work, and control them.',
        must: [
          'The documents the standard requires: scope, policy, aspects, compliance obligations, objectives, emergency response, monitoring and compliance records',
          'Any other documents you need to run the system',
          'All of it controlled, as 7.5.1 to 7.5.3 describe',
        ],
        auditor:
          'That every document they are shown is the current issue and that records are complete and retrievable.',
      },
      {
        num: 'Cl. 7.5.1',
        title: 'Documented information — general',
        freq: 'Review: annually',
        requires:
          'The environmental system has to include the documented information the standard asks for, plus whatever you decide is needed. How much depends on the size of the business, the complexity of what it does and the competence of its people.',
        must: [
          'A management system manual or equivalent that sets out the system',
          'A list of the controlled documents in use',
          'Records kept wherever the standard calls for them',
        ],
        auditor:
          'That the amount of paperwork fits the business — enough to show control, not so much that nobody reads it.',
      },
      {
        num: 'Cl. 7.5.2',
        title: 'Creating and updating',
        freq: 'Each time a document changes',
        requires:
          'Documents have to be identified (title, number, issue, date), in a suitable format, and reviewed and approved before they are used.',
        must: [
          'Every controlled document numbered, dated and at a known issue',
          'An approver recorded for each issue',
          'Changes reviewed before the new issue is released',
        ],
        auditor:
          'That the issue on the page matches the register and that someone with authority approved it.',
      },
      {
        num: 'Cl. 7.5.3',
        title: 'Control of documented information',
        freq: 'Ongoing',
        requires:
          'Documents must be available where they are needed, protected, and controlled through distribution, storage, changes, retention and disposal. Documents from outside, such as permits, have to be identified and controlled too.',
        must: [
          'Old issues withdrawn from use',
          'Retention periods for environmental records',
          'External documents — permits, licences, consents — identified and kept current',
        ],
        auditor:
          'That an out-of-date procedure is not in use on site and that records are kept for the period you or the law set.',
      },
      {
        num: 'Cl. 8.1',
        title: 'Operational planning and control',
        freq: 'Ongoing — records per event',
        requires:
          'You must control the operations linked to your significant aspects and compliance obligations, set the criteria they must meet, and control or influence anything you outsource. Looking across the life of what you provide, you also set environmental requirements for purchasing and for contractors, and consider the impacts of transport, use and disposal.',
        must: [
          'Operating controls for each significant aspect — procedures, instructions, criteria',
          'Waste carried and disposed of only by carriers and sites authorised to take it, with the transfer records kept for as long as the law requires',
          'Hazardous substances assessed, stored and handled as their assessments require',
          'Environmental requirements passed to suppliers and contractors',
        ],
        auditor:
          'Your records for recent waste and how hazardous substances are stored. They will check that waste went to an authorised carrier and was described correctly: using an unauthorised one is an offence and a major nonconformity.',
      },
      {
        num: 'Cl. 8.2',
        title: 'Emergency preparedness and response',
        freq: 'Test: periodically, at least annually is usual',
        requires:
          'You have to plan how you will respond to the emergencies you identified in 6.1 so that harm to the environment is prevented or reduced, respond when one happens, test the planned response where practical, review it after a test or an emergency, and give the people involved the information and training they need.',
        must: [
          'An emergency preparedness and response procedure covering each emergency type',
          'Spill kits and other response equipment in place and checked',
          'Tests and drills recorded, with what was learned',
          'The procedure reviewed after every test or real emergency',
        ],
        auditor:
          'They will ask someone where the spill kit is and how to use it, then look for the last test of the response and what changed because of it.',
      },
      {
        num: 'Cl. 9.1',
        title: 'Monitoring, measurement, analysis and evaluation',
        freq: 'Ongoing — reviewed at least annually',
        requires:
          'You have to decide what environmental performance to monitor and measure, how, against what criteria and indicators, and when the results are analysed. This clause is met through 9.1.1 and 9.1.2.',
        must: [
          'The measures that show your environmental performance',
          'The results analysed and reported to top management',
          'Evaluation of compliance with your obligations (9.1.2)',
        ],
        auditor:
          'That you can show the trend in your main measures and explain it.',
      },
      {
        num: 'Cl. 9.1.1',
        title: 'Monitoring and measuring environmental performance',
        freq: 'Monthly is usual — reviewed at the management review',
        requires:
          'You have to monitor and measure what matters to your environmental performance — usually energy, fuel, water, waste and emissions — using calibrated or verified equipment where it matters, evaluate the results, and communicate them inside the business and outside it where needed.',
        must: [
          'Regular readings of energy, fuel, water and waste, with emissions worked out from them',
          'Comparison against last year and against your objectives',
          'Meters and monitoring equipment calibrated or verified where accuracy matters',
          'Results reviewed and acted on',
        ],
        auditor:
          'Your figures for the last year, how they are gathered, and whether anyone looked at them and did something when they went the wrong way.',
      },
      {
        num: 'Cl. 9.1.2',
        title: 'Evaluation of compliance',
        freq: 'At the frequency you set for each obligation',
        requires:
          'You have to decide how often you check that you are meeting each compliance obligation, check it, act when you are not, and keep up to date with where you stand.',
        must: [
          'A frequency set for evaluating each obligation',
          'A record of each evaluation: date, result, who checked',
          'Action taken where an obligation was not met',
        ],
        auditor:
          'Evaluations that are up to date. A legal register that lists the law but shows no check that you comply with it is the usual finding.',
      },
      {
        num: 'Cl. 9.2',
        title: 'Internal Audit',
        freq: 'At least annually',
        requires:
          'Same principle as ISO 9001 — planned internal audit covering all EMS clauses, completed before the certifying body’s surveillance visit.',
        must: [
          'Internal audit schedule — planned in advance',
          'Audit report covering all clauses — findings documented',
          'Corrective actions for all findings — with closure evidence',
        ],
        auditor:
          'As with ISO 9001, not having completed an internal audit is a major finding. If you run a BMS, one combined audit can cover ISO 9001, 14001 and 45001 simultaneously.',
      },
      {
        num: 'Cl. 9.3',
        title: 'Management Review',
        freq: 'At least annually',
        requires:
          'Management must review the EMS at planned intervals, covering environmental performance, objective progress, legal compliance status, changes in aspects and obligations, complaints, and continual improvement opportunities.',
        must: [
          'Written management review record — including environmental performance data',
          'Review of progress against environmental objectives',
          'Confirmation of legal compliance',
          'Actions agreed with owners and dates',
        ],
        auditor:
          'That the review happened, that environmental performance data was presented and discussed, and that previous actions are complete. If running a BMS, one combined management review covers all standards.',
      },
      {
        num: 'Cl. 10.1',
        title: 'Improvement — general',
        freq: 'Ongoing',
        requires:
          'You have to look for opportunities to improve and act on them so the system achieves what it is meant to, using what monitoring, audits and the management review tell you.',
        must: [
          'Improvement opportunities identified at the management review',
          'Actions raised with owners and dates',
          'Evidence they were carried out',
        ],
        auditor:
          'Something that has got better since the last visit, and the record of how it was decided.',
      },
      {
        num: 'Cl. 10.2',
        title: 'Nonconformity and corrective action',
        freq: 'Each time something goes wrong',
        requires:
          'When something goes wrong — a spillage, a missed check, a breached permit, an audit finding — you must deal with it and its environmental consequences, find the cause, act so it does not happen again, check the action worked, and change the system if needed.',
        must: [
          'Each nonconformity recorded with what was done immediately',
          'Root cause found and corrective action taken',
          'The effectiveness of the action reviewed before closing',
        ],
        auditor:
          'A recent incident followed from start to close. Closing an action without checking it worked is a common finding.',
      },
      {
        num: 'Cl. 10.3',
        title: 'Continual improvement',
        freq: 'Ongoing — reviewed annually',
        requires:
          'You have to keep improving how suitable, adequate and effective the environmental system is, so that your environmental performance gets better over time.',
        must: [
          'Environmental performance improving year on year, or a reason why not',
          'Improvement decisions recorded at the management review',
          'Objectives renewed as old ones are met',
        ],
        auditor:
          'Year-on-year figures that show the system is moving, not standing still.',
      },
    ],
  },
  iso_27001: {
    icon: '🔐',
    title: 'ISO 27001:2022 — Information Security Management System',
    facts: [
      { label: 'Certification cycle', value: '3 years' },
      { label: 'Surveillance audits', value: 'Year 1 and Year 2' },
      { label: 'Key focus', value: 'Confidentiality, Integrity, Availability' },
    ],
    overview: [
      'ISO 27001 is about managing the security of your information — customer data, employee data, financial records, business-sensitive information. It does not require you to have no security incidents. It requires you to know what information you hold, understand the risks to it, implement controls and continually improve.',
      'For a business like yours, the key information assets are: customer project data and pricing, employee personal records, financial records, supplier information and any data you hold about sites you work on. The risks include: unauthorised access to laptops or systems, accidental data disclosure, phishing attacks and loss of data.',
      'The 2022 revision of the standard placed greater emphasis on threat intelligence and information security for cloud services — if your business uses cloud-based software (which all JeanieIQ users do), your asset register and risk assessment must reflect this.',
    ],
    clauses: [
      {
        num: 'Cl. 5.2',
        title: 'Information Security Policy',
        freq: 'Review: annually',
        requires:
          "A documented information security policy signed by top management. It must set out the organisation's approach to protecting information, the objectives of the ISMS and commitment to continual improvement.",
        must: [
          'Signed information security policy — director signature, current date',
          'Policy communicated to all employees — acknowledgement records',
          'Policy reviewed annually — or when significant changes occur',
          'Supporting policies if required: acceptable use, clear desk, password policy',
        ],
        auditor:
          'That the policy is current, signed and that all employees are aware of it. They may interview staff to check awareness. A policy that exists but no one has read is a finding.',
      },
      {
        num: 'Cl. 6.1.2',
        title: 'Information Security Risk Assessment',
        freq: 'Review: annually or on significant change',
        requires:
          'A documented risk assessment process that identifies information security risks, assesses their likelihood and impact, and determines how each risk will be treated — accepted, mitigated, transferred or avoided.',
        must: [
          'Risk register listing all identified information security risks',
          'Likelihood and impact rating for each risk',
          'Treatment decision for each risk — with justification',
          'Risk treatment plan showing controls implemented',
          'Risk assessment reviewed annually and after any significant incident or change',
        ],
        auditor:
          'That your risk assessment is comprehensive — covering all your information assets — and that treatment decisions are realistic. A risk register that lists risks as "low" without justification, or that ignores obvious risks like phishing, will be challenged.',
      },
      {
        num: 'Cl. 7.2',
        title: 'Competence and Security Awareness',
        freq: 'Review: annually',
        requires:
          'All employees who affect information security must be competent to do so and must be aware of the information security policy, their contribution to the ISMS and the consequences of not conforming.',
        must: [
          'Security awareness training for all employees — annual refresher',
          'Training completion records — JeanieIQ Training Records module',
          'Phishing simulation or similar awareness activity — records kept',
          'New starter information security induction records',
        ],
        auditor:
          'Training records and may ask employees about their understanding of information security responsibilities. Employees who cannot explain basic security requirements — like how to report a suspected breach — will generate findings.',
      },
      {
        num: 'Cl. 8.1',
        title: 'Asset Management',
        freq: 'Review: annually and on change',
        requires:
          'All information assets must be identified, classified by sensitivity and have an assigned owner responsible for their protection. This includes hardware, software, data, people and facilities.',
        must: [
          'Asset register listing all information assets — laptops, servers, cloud services, paper records, data stores',
          'Classification for each asset: Public / Internal / Confidential / Restricted',
          'Asset owner assigned for each item',
          'Register updated when assets are added, changed or disposed of',
        ],
        auditor:
          'That your register is comprehensive and current. They will look for cloud services — SaaS tools your business uses are information assets and must be on the register. Missing assets will be raised.',
      },
      {
        num: 'Cl. 9.1',
        title: 'Monitoring and Measurement',
        freq: 'Ongoing — review quarterly',
        requires:
          'You must monitor and measure the performance of the ISMS against defined criteria and evaluate results. This includes monitoring security controls to confirm they are working effectively.',
        must: [
          'Access control review — who has access to what — reviewed quarterly',
          'Incident log — all security incidents or near-misses recorded',
          'Security metrics — number of incidents, awareness training completion, access violations',
          'Review records showing metrics were analysed and acted upon',
        ],
        auditor:
          'That you are actively monitoring your ISMS — not just setting it up and forgetting it. Access review records and incident logs will be checked. If you have had no security incidents and no access reviews, the auditor will question whether you are monitoring at all.',
      },
      {
        num: 'Cl. 9.2',
        title: 'Internal Audit',
        freq: 'At least annually',
        requires:
          'Planned internal audit of the ISMS covering all clauses of the standard, completed before each surveillance visit.',
        must: [
          'Internal audit schedule — planned, not reactive',
          'Audit report covering all ISMS clauses',
          'Corrective actions for all findings with evidence of closure',
        ],
        auditor:
          'Audit report and whether findings have been closed. ISO 27001 audits often go deeper into technical controls than other standards — your internal auditor should have sufficient knowledge of the standard.',
      },
      {
        num: 'Cl. 9.3',
        title: 'Management Review',
        freq: 'At least annually',
        requires:
          'Top management reviews the ISMS annually. Inputs must include security performance data, risk assessment results, audit findings and changes that could affect the ISMS.',
        must: [
          'Written management review record — attendees, inputs reviewed, decisions and actions',
          'Review of security metrics and incident log',
          'Review of risk register — any new risks or changed risk ratings',
          'Actions agreed with owners and target dates',
        ],
        auditor:
          'That management is genuinely engaged with information security — not just delegating it entirely to IT. Senior management understanding of key risks and performance metrics will be tested.',
      },
    ],
  },
  iso_45001: {
    icon: '⛑️',
    title: 'ISO 45001:2018 — Occupational Health & Safety Management System',
    facts: [
      { label: 'Certification cycle', value: '3 years' },
      { label: 'UK legal baseline', value: 'Health & Safety at Work Act 1974' },
      { label: 'Replaces', value: 'OHSAS 18001:2007' },
    ],
    overview: [
      'ISO 45001 builds on your legal health and safety obligations under the Health and Safety at Work Act 1974 and associated regulations. It does not replace them — it provides a systematic framework for managing OH&S that goes beyond minimum legal compliance.',
      "The standard requires worker participation as a genuine principle — not just consultation. It requires you to proactively identify hazards, assess risks, implement controls and learn from incidents. The Compliance Gate — JeanieIQ's core differentiator — directly supports ISO 45001 by enforcing qualification requirements at the point of work.",
      'Your JeanieIQ platform already collects the vast majority of your ISO 45001 evidence automatically. Risk assessments, toolbox talk attendance, training completions, incident reports and compliance gate events all file automatically to the evidence store. Your current evidence score reflects this.',
    ],
    clauses: [
      {
        num: 'Cl. 5.2',
        title: 'OH&S Policy',
        freq: 'Review: annually',
        requires:
          'A signed OH&S policy committing to providing safe and healthy working conditions, fulfilling legal obligations, eliminating hazards and reducing risks, and continual improvement.',
        must: [
          'Signed OH&S policy — director signature, current date — displayed in the workplace',
          'Policy communicated to all employees including contractors and visitors',
          'Policy reviewed annually',
        ],
        auditor:
          'That the policy is displayed, current and signed. They will ask employees if they know where to find it. A policy locked in a filing cabinet that no one can access is a finding.',
      },
      {
        num: 'Cl. 6.1',
        title: 'Risk Assessment',
        freq: 'Review: on task change / annually',
        requires:
          'You must identify hazards, assess the risks associated with them and determine controls. The hierarchy of control must be applied — elimination first, then substitution, engineering controls, administrative controls, PPE last.',
        must: [
          'Risk assessment for every task your employees carry out — JeanieIQ H&S module',
          'Hierarchy of controls documented for each significant risk',
          'RAs reviewed when the task, equipment or circumstances change — and at least annually',
          'Employees briefed on RAs relevant to their work — toolbox talk record',
        ],
        auditor:
          'Comprehensiveness and currency of your RA library. They will select specific tasks and ask to see the RA, then check that the controls documented are actually in use. Out-of-date RAs and RAs that are not communicated to workers are common findings.',
      },
      {
        num: 'Cl. 6.1.2',
        title: 'COSHH — Hazardous Substances',
        freq: 'Review: on substance change / annually',
        requires:
          'Under COSHH Regulations 2002 (part of your ISO 45001 evidence), you must identify all hazardous substances in your workplace, assess the risks of exposure and implement controls.',
        must: [
          'COSHH register — all hazardous substances listed with Safety Data Sheet (SDS) on file',
          'COSHH assessment for each substance — JeanieIQ H&S module',
          'Exposure controls documented and in place',
          'Health surveillance where required (e.g. isocyanates, lead, silica)',
          'WEL monitoring where required',
        ],
        auditor:
          'Your COSHH register and whether assessments are current. They will walk your site and identify substances — any substance in use without a COSHH assessment is a finding. Health surveillance records will be checked where relevant.',
      },
      {
        num: 'Cl. 7.2',
        title: 'Competence',
        freq: 'Ongoing — checked at every clock-in',
        requires:
          'Workers must be competent for the hazards they face. You must determine required competence, provide training where needed, retain evidence and take action when competence gaps are identified.',
        must: [
          'Training matrix — all roles vs required qualifications',
          'Qualification records for all employees — with expiry dates',
          'Compliance Gate configured to block access on qualification expiry — JeanieIQ core feature',
          'Refresher training scheduled before expiry — JeanieIQ alerts handle this',
        ],
        auditor:
          'Training records for a sample of operatives, particularly those operating plant or working at height. They will cross-check required qualifications against those on file. The Compliance Gate audit log is direct evidence that expired qualifications resulted in access being blocked — this is powerful evidence for the auditor.',
      },
      {
        num: 'Cl. 7.3',
        title: 'Awareness — Toolbox Talks',
        freq: 'Regular — minimum monthly recommended',
        requires:
          'Workers must be aware of the OH&S policy, hazards and risks relevant to their work, OH&S procedures, and the consequences of not conforming with the OHSMS.',
        must: [
          'Regular toolbox talks — topic, date, attendees, content — JeanieIQ Training Records',
          'Biometric sign-off on attendance — JeanieIQ handles this automatically',
          'Records kept permanently — auditors may ask for records from previous years',
        ],
        auditor:
          'Toolbox talk records and attendance. They will check regularity — toolbox talks that stopped 6 months ago suggest the OHSMS is not being maintained. Biometric sign-off records are strong evidence that workers were genuinely present.',
      },
      {
        num: 'Cl. 8.2',
        title: 'Emergency Preparedness and Incident Reporting',
        freq: 'Review: after every incident / annually',
        requires:
          'You must prepare for and respond to emergency situations. When incidents occur — including near misses — you must investigate them, determine root cause and implement corrective action.',
        must: [
          'Emergency procedures — fire, first aid, chemical spill, serious injury',
          'Incident and near miss log — all events recorded — JeanieIQ H&S module',
          'Investigation records with root cause and corrective action',
          'RIDDOR notifications where applicable under The Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013',
        ],
        auditor:
          'Your incident log for the past year and a sample of investigation records. They will check that near misses are recorded — not just reportable injuries. A business with zero near misses on record is suspicious — it usually means near misses are not being reported, which is a finding in itself.',
      },
      {
        num: 'Cl. 9.1',
        title: 'Monitoring — Compliance Gate Data',
        freq: 'Ongoing — reviewed monthly',
        requires:
          'You must monitor, measure, analyse and evaluate OH&S performance. This includes monitoring legal compliance and the effectiveness of controls.',
        must: [
          'OH&S performance metrics — incidents, near misses, gate blocks, training compliance',
          'Compliance Gate audit log — every block, clearance and override — JeanieIQ',
          'Records showing metrics were reviewed and acted upon',
        ],
        auditor:
          'That you are actively monitoring OH&S performance — not just recording incidents when they happen. The Compliance Gate audit log is direct, timestamped evidence of systematic monitoring and is one of the strongest evidence items you can present for this clause.',
      },
      {
        num: 'Cl. 9.2',
        title: 'Internal Audit',
        freq: 'At least annually',
        requires:
          'Planned internal audit of the OHSMS covering all clauses. Workers must be involved in the process — ISO 45001 places greater emphasis on worker participation than earlier standards.',
        must: [
          'Internal audit schedule with worker involvement noted',
          'Audit report covering all clauses including site-based observations',
          'Corrective actions for all findings — closed before the surveillance visit',
        ],
        auditor:
          'Audit report and whether workers were involved in the process — not just managers auditing their own areas. Evidence of worker participation (interview notes, worker signatures on audit documents) is expected.',
      },
      {
        num: 'Cl. 9.3',
        title: 'Management Review',
        freq: 'At least annually',
        requires:
          'Top management reviews the OHSMS annually. Inputs must include OH&S performance data, incident trends, audit findings and worker feedback.',
        must: [
          'Written management review record — attendees, all required inputs covered, actions agreed',
          'Review of incident data and trends — any patterns requiring action',
          'Review of compliance gate data — blocks, overrides, patterns',
          'Actions agreed with owners, dates and evidence of closure',
        ],
        auditor:
          'That management engagement with OH&S is genuine. They will check whether actions from previous reviews were completed and whether the review produced real improvement decisions — not just statements that "performance is satisfactory."',
      },
    ],
  },
};

/**
 * The clauses a tenant may declare OUT OF SCOPE, per standard.
 *
 * ISO 9001 allows a requirement to be excluded where it is not applicable, and
 * the exclusion plus its justification are recorded against the scope (4.3).
 * Design and development is the common and legitimate one — a company that
 * builds to a customer's specification designs nothing.
 *
 * DELIBERATELY JUST 8.3 TODAY, and that is not timidity. `scopeExclusions` is a
 * single free-text box whose own placeholder reads "Clause 8.3 Design and
 * development is excluded because …", and the Quality Manual has only ever
 * recognised 8.3. Recognising more clauses from that prose would change what is
 * excluded for tenants who have already written in the box, which is a decision
 * for the owner rather than a side effect of a bug fix. The structured picker
 * this wants in the end is logged on TODO 119.
 */
export const EXCLUDABLE_CLAUSES: Partial<Record<IsoStandard, readonly string[]>> = {
  iso_9001: ['8.3'],
};

/**
 * Which clauses this tenant has excluded from scope.
 *
 * ONE SOURCE, and that is the whole point of it existing (2026-09-21). The rule
 * lived inline in the Quality Manual generator and nowhere else, so the manual
 * said a clause was excluded while the readiness roadmap told the tenant to
 * "Upload evidence for clause 8.3", the score counted it against them, and the
 * evidence pack handed to the certifying body listed it as "Not yet evidenced".
 * A certifying body received two controlled documents that contradicted each
 * other, and the second invited a nonconformity against a clause that did not
 * apply.
 *
 * The matching rule is unchanged from the manual's: the clause number appearing
 * anywhere in the justification means excluded. It is a blunt test on free
 * prose — "clause 8.3 is NOT excluded" would match it — and it is kept exactly
 * as it was so this fix changes WHERE the answer is read, not WHAT it says.
 */
export function excludedClausesFrom(
  standard: IsoStandard,
  scopeExclusions: string | null | undefined,
): string[] {
  const text = (scopeExclusions ?? '').toLowerCase();
  if (!text.trim()) return [];
  const parents = (EXCLUDABLE_CLAUSES[standard] ?? []).filter((c) => text.includes(c));
  if (parents.length === 0) return [];
  /*
   * A SUB-CLAUSE GOES WITH ITS PARENT (2026-10-03). The first clause-map row
   * under 8.3 arrived with the design-output evidence (8.3.5). Without this, a
   * tenant that had correctly excluded design would have seen 8.3 excluded in
   * the manual and "upload evidence for 8.3.5" on the roadmap, which is the
   * exact contradiction the 2026-09-21 fix removed. Listed from the map, so
   * every surface that filters map rows by this list excludes them too.
   */
  const children = (CLAUSE_MAP_BY_STANDARD[standard] ?? [])
    .map((r) => r.clause)
    .filter((c) => parents.some((p) => c.startsWith(`${p}.`)));
  return [...parents, ...children];
}

/**
 * The standard a stored evidence row belongs to, as its key. Some writers store a
 * label ("ISO 9001:2015", "ISO 45001") rather than the key, so every reader
 * compares this, never the stored value.
 */
export function isoStandardKey(s: string | null | undefined): string {
  const v = (s ?? '').toLowerCase();
  if (v.includes('9001')) return 'iso_9001';
  if (v.includes('14001')) return 'iso_14001';
  if (v.includes('27001')) return 'iso_27001';
  if (v.includes('45001')) return 'iso_45001';
  return v;
}

/** Evidence filed against `evidenceClause` counts for the map row `mapClause`: the same clause, or a sub-clause of it. */
export function clauseCovers(mapClause: string, evidenceClause: string): boolean {
  return evidenceClause === mapClause || evidenceClause.startsWith(`${mapClause}.`);
}

export interface ClauseCoverage {
  /** The standard's map rows, less what the tenant has excluded. */
  required: string[];
  /** Those with live evidence against them or a sub-clause of them. */
  evidenced: string[];
  /** Those with none. */
  needed: string[];
}

/**
 * Which rows of a standard's clause map are evidenced. The one rule for the
 * score, the roadmap, the clause map, the audit review and its export: a
 * parent row (7.5) counts when it or any sub-clause (7.5.2) has evidence, and
 * evidence against a clause not on the map counts for nothing. Counted from the
 * clause map, never a hardcoded number of clauses.
 */
export function clauseCoverage(
  standard: IsoStandard,
  evidenceClauses: Iterable<string>,
  excluded: readonly string[] = [],
): ClauseCoverage {
  const have = [...new Set(evidenceClauses)];
  const required = (CLAUSE_MAP_BY_STANDARD[standard] ?? [])
    .map((r) => r.clause)
    .filter((c) => !excluded.includes(c));
  const evidenced = required.filter((c) => have.some((e) => clauseCovers(c, e)));
  return { required, evidenced, needed: required.filter((c) => !evidenced.includes(c)) };
}

/** Is this clause excluded from the tenant's scope? A sub-clause is, when its parent is. */
export function isClauseExcluded(
  standard: IsoStandard,
  clause: string,
  scopeExclusions: string | null | undefined,
): boolean {
  return excludedClausesFrom(standard, scopeExclusions).some(
    (c) => clause === c || clause.startsWith(`${c}.`),
  );
}
